import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Plus, Edit, Trash2, Factory, Package, FlaskConical, ShoppingCart, BarChart3, UserCheck, TrendingUp, Shield, FileDown, Lock, LockOpen, Receipt as ReceiptIcon, Gift, Printer, BookOpen, Truck } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import FactoryPOS from "./FactoryPOS";
import CombosTab from "./factory/CombosTab";
import CatalogTab from "./factory/CatalogTab";
import DeliveriesTab from "./factory/DeliveriesTab";
import ProductPhotoUpload from "./factory/ProductPhotoUpload";
import ClientProductGoals from "./factory/ClientProductGoals";
import { SaleReceiptContent, saleToReceipt, printReceiptNode } from "./factory/SaleReceipt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PRICE_LABEL = { atacado: "Atacado", varejo: "Varejo", especial: "Especial" };

const EMPTY_MAT = { name: "", description: "", unit: "kg", unit_cost: "", quantity: "", supplier_id: "" };
const EMPTY_PROD = {
  name: "", description: "", unit: "saco", cost_mode: "recipe", manual_cost: "",
  recipe: [{ raw_material_id: "", quantity: "" }],
  tax_percent: "", overhead_cost: "", profit_margin_percent: "",
  price_wholesale: "", price_retail: "", price_special: "",
  barcode: "",
};
const EMPTY_SALE_ITEM = { product_id: "", quantity: "", price_type: "varejo", unit_price: "" };

const PAYMENT_LABEL = { dinheiro: "Dinheiro", pix: "PIX", cartao: "Cartão", cartao_debito: "Débito", cartao_credito: "Crédito", aprazo: "A Prazo" };
const todayPlusDays = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };
// Datas do backend chegam em UTC (naive, sem 'Z'). Interpretamos como UTC e mostramos em Brasília.
const fmtDateTimeBR = (iso) => {
  if (!iso) return "—";
  const s = typeof iso === "string"
    ? (iso.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z")
    : iso;
  return new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
};

export default function FactoryModule({ companyId, companyName = "", clients = [], permissions = [], isAdmin = false, bankAccounts = [] }) {
  const can = (code) => isAdmin || permissions.includes(code);
  const canManageMat = can("manage_factory_materials");
  const canManageProd = can("manage_factory_products");
  const canProduce = can("create_factory_production");
  const canSell = can("create_factory_sales");
  const canUsePos = can("use_factory_pos");
  const canViewReports = can("view_factory_reports");
  const canApproveCredit = can("approve_factory_credit");
  const canDeliveries = can("manage_factory_deliveries") || can("factory_delivery_driver");
  const driverOnly = !isAdmin && can("factory_delivery_driver") && !["view_factory", "manage_factory_materials", "manage_factory_products", "create_factory_production", "create_factory_sales", "use_factory_pos", "view_factory_reports", "approve_factory_credit", "manage_factory_deliveries"].some(c => permissions.includes(c));

  const [tab, setTab] = useState("materias");
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [productions, setProductions] = useState([]);
  const [sales, setSales] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [report, setReport] = useState(null);
  const [clientsCredit, setClientsCredit] = useState([]);
  const [creditDlg, setCreditDlg] = useState({ open: false, client: null, credit_limit: "", max_days: 20, max_installments: 1, notes: "" });
  const [employees, setEmployees] = useState([]);
  const [reportStart, setReportStart] = useState("");
  const [reportEnd, setReportEnd] = useState("");
  const [goals, setGoals] = useState([]);
  const [goalDlg, setGoalDlg] = useState({ open: false, data: null, id: null });
  const [prodGoals, setProdGoals] = useState([]);
  const [prodGoalDlg, setProdGoalDlg] = useState({ open: false, data: null, id: null });
  const [adminReport, setAdminReport] = useState(null);
  const [adminStart, setAdminStart] = useState("");
  const [adminEnd, setAdminEnd] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);
  const adminPanelRef = useRef(null);

  const [matDlg, setMatDlg] = useState({ open: false, data: null, id: null });
  const [prodDlg, setProdDlg] = useState({ open: false, data: null, id: null });
  const [prodDlgOpen, setProdDlgOpen] = useState(false);
  const [productionDlg, setProductionDlg] = useState({ open: false, product_id: "", quantity: "", is_adjustment: false });
  const [saleDlg, setSaleDlg] = useState({ open: false, data: null });
  const [entryDlg, setEntryDlg] = useState({ open: false, data: null });
  const [posOpen, setPosOpen] = useState(false);
  const [dupReceipt, setDupReceipt] = useState(null);
  const dupReceiptRef = useRef(null);
  // Caixa (sessions)
  const [cashSessions, setCashSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [closeSessionDlg, setCloseSessionDlg] = useState({ open: false, session: null, counted: "", notes: "" });
  const [cashMovements, setCashMovements] = useState([]);
  const [movDlg, setMovDlg] = useState({ open: false, type: "sangria", amount: "", reason: "", bank_account_id: "" });
  // Fiscal config
  const [fiscalDlg, setFiscalDlg] = useState({ open: false, data: null });
  // NFC-e pendentes
  const [pendingNfce, setPendingNfce] = useState([]);

  const fetchAll = async () => {
    if (!companyId) return;
    try {
      const [mats, prods, prodhist, sls, sups, mainSups, cc, emps] = await Promise.all([
        axios.get(`${API}/companies/${companyId}/factory/raw-materials`),
        axios.get(`${API}/companies/${companyId}/factory/products`),
        axios.get(`${API}/companies/${companyId}/factory/production`),
        axios.get(`${API}/companies/${companyId}/factory/sales`),
        axios.get(`${API}/companies/${companyId}/distributor/suppliers`).catch(() => ({ data: [] })),
        axios.get(`${API}/companies/${companyId}/suppliers`).catch(() => ({ data: [] })),
        axios.get(`${API}/companies/${companyId}/factory/clients-credit`).catch(() => ({ data: [] })),
        axios.get(`${API}/companies/${companyId}/employees`).catch(() => ({ data: [] })),
      ]);
      setMaterials(mats.data || []);
      setProducts(prods.data || []);
      setProductions(prodhist.data || []);
      setSales(sls.data || []);
      // Unifica fornecedores (distribuidora + principal) sem duplicar
      const map = new Map();
      [...(sups.data || []), ...(mainSups.data || [])].forEach(s => { if (s && s.id) map.set(s.id, s); });
      setSuppliers(Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setClientsCredit(cc.data || []);
      setEmployees(emps.data || []);
    } catch (e) {
      toast.error("Erro ao carregar dados da fábrica");
    }
  };

  useEffect(() => { if (!driverOnly) fetchAll(); }, [companyId]);
  // Atualiza a lista de vendas ao voltar para a aba (status de entrega pode ter mudado)
  useEffect(() => {
    if (tab === "vendas" && !driverOnly) axios.get(`${API}/companies/${companyId}/factory/sales`).then(r => setSales(r.data || []));
  }, [tab]);

  useEffect(() => {
    if (tab === "relatorio" && canViewReports) {
      const params = new URLSearchParams();
      if (reportStart) params.append("start_date", reportStart);
      if (reportEnd) params.append("end_date", reportEnd);
      axios.get(`${API}/companies/${companyId}/factory/report?${params.toString()}`).then(r => setReport(r.data));
    }
  }, [tab, companyId, canViewReports, reportStart, reportEnd]);

  useEffect(() => {
    if (tab === "metas" && companyId) {
      axios.get(`${API}/companies/${companyId}/factory/sales-goals`).then(r => setGoals(r.data || []));
    }
    if (tab === "producao" && companyId) {
      axios.get(`${API}/companies/${companyId}/factory/production-goals`).then(r => setProdGoals(r.data || []));
    }
    if (tab === "admin" && companyId) {
      const params = new URLSearchParams();
      if (adminStart) params.append("start_date", adminStart);
      if (adminEnd) params.append("end_date", adminEnd);
      axios.get(`${API}/companies/${companyId}/factory/admin-report?${params.toString()}`).then(r => setAdminReport(r.data));
    }
  }, [tab, companyId, adminStart, adminEnd]);

  // ----- Operator Ranking (PDV) -----
  const [operatorPeriod, setOperatorPeriod] = useState("day");
  const [operatorRanking, setOperatorRanking] = useState(null);
  const loadOperatorRanking = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/${companyId}/factory/operator-ranking?period=${operatorPeriod}`);
      setOperatorRanking(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    if (tab === "admin") loadOperatorRanking();
  }, [tab, companyId, operatorPeriod]);

  // ----- Caixa (sessions) -----
  const loadCashSessions = async () => {
    try {
      const [curRes, listRes, pendingRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}/factory/cash-sessions/current`),
        axios.get(`${API}/companies/${companyId}/factory/cash-sessions?limit=30`),
        axios.get(`${API}/companies/${companyId}/factory/nfce-pending`).catch(() => ({ data: [] })),
      ]);
      setCurrentSession(curRes.data || null);
      setCashSessions(listRes.data || []);
      setPendingNfce(pendingRes.data || []);
      if (curRes.data?.id) {
        const movs = await axios.get(`${API}/companies/${companyId}/factory/cash-sessions/${curRes.data.id}/movements`).catch(() => ({ data: [] }));
        setCashMovements(movs.data || []);
      } else {
        setCashMovements([]);
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    if (tab === "caixa") loadCashSessions();
  }, [tab, companyId]);

  const saveCashMovement = async () => {
    if (!currentSession) return;
    const amt = parseFloat(movDlg.amount);
    if (!amt || amt <= 0) { toast.error("Informe um valor válido"); return; }
    try {
      await axios.post(`${API}/companies/${companyId}/factory/cash-sessions/${currentSession.id}/movements`, {
        type: movDlg.type,
        amount: amt,
        reason: movDlg.reason || null,
        bank_account_id: movDlg.bank_account_id || null,
      });
      toast.success(movDlg.type === "sangria" ? "Sangria registrada" : "Suprimento registrado");
      setMovDlg({ open: false, type: "sangria", amount: "", reason: "", bank_account_id: "" });
      loadCashSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar movimentação");
    }
  };

  const retryNfce = async (saleId) => {
    try {
      const { data } = await axios.post(`${API}/companies/${companyId}/factory/sales/${saleId}/nfce/retry`);
      if (data.status === "autorizado") toast.success("NFC-e autorizada!");
      else toast.info(`NFC-e: ${data.status}`);
      loadCashSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail?.mensagem_sefaz || e.response?.data?.detail || "Erro na reemissão");
    }
  };
  const closeCashSession = async () => {
    const sess = closeSessionDlg.session;
    if (!sess) return;
    try {
      await axios.post(`${API}/companies/${companyId}/factory/cash-sessions/${sess.id}/close`, {
        counted_cash: Number(closeSessionDlg.counted || 0),
        notes: closeSessionDlg.notes || null,
      });
      toast.success("Caixa fechado");
      setCloseSessionDlg({ open: false, session: null, counted: "", notes: "" });
      loadCashSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao fechar caixa");
    }
  };

  // ----- Config Fiscal -----
  const openFiscalDlg = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/${companyId}/factory/fiscal-config`);
      setFiscalDlg({ open: true, data: data && Object.keys(data).length ? data : {
        cnpj_emitente: "", uf: "PB", municipio_codigo: "",
        regime_tributario: 1, ncm_default: "38245000", cfop_default: "5102",
        csosn_default: "102", origem_default: 0, unidade_default: "SC",
        presenca_comprador: 1, local_destino: 1, modalidade_frete: 9,
      }});
    } catch (e) {
      toast.error("Erro ao carregar configuração fiscal");
    }
  };
  const saveFiscalConfig = async () => {
    const d = fiscalDlg.data || {};
    if (!d.cnpj_emitente) { toast.error("CNPJ do emitente é obrigatório"); return; }
    try {
      await axios.put(`${API}/companies/${companyId}/factory/fiscal-config`, {
        ...d,
        regime_tributario: Number(d.regime_tributario || 1),
        origem_default: Number(d.origem_default ?? 0),
        presenca_comprador: Number(d.presenca_comprador ?? 1),
        local_destino: Number(d.local_destino ?? 1),
        modalidade_frete: Number(d.modalidade_frete ?? 9),
      });
      toast.success("Configuração fiscal salva");
      setFiscalDlg({ open: false, data: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  // ----- Formatação de meses (ex: 2025-03 -> Mar/25) -----
  const MONTH_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const fmtMonth = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    return `${MONTH_SHORT[parseInt(m) - 1] || m}/${y.slice(2)}`;
  };
  const monthlyChartData = useMemo(() => {
    return (adminReport?.monthly_series || []).map(x => ({ ...x, label: fmtMonth(x.month) }));
  }, [adminReport]);

  // ----- Exportar PDF do Painel -----
  const exportAdminPdf = async () => {
    if (!adminPanelRef.current) return;
    try {
      setExportingPdf(true);
      // Pequeno delay para garantir que todos os gráficos estão renderizados
      await new Promise(res => setTimeout(res, 350));
      const node = adminPanelRef.current;
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: node.scrollWidth,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const availW = pageWidth - margin * 2;
      // Cabeçalho
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text(`Painel Administrativo · Fábrica${companyName ? " – " + companyName : ""}`, margin, 12);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      const periodTxt = (adminStart || adminEnd)
        ? `Período: ${adminStart || "Início"} a ${adminEnd || "Hoje"}`
        : "Período: Todos os registros";
      pdf.text(periodTxt, margin, 18);
      pdf.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, margin, 23);
      // Imagem (pode ocupar várias páginas)
      const imgProps = pdf.getImageProperties(imgData);
      const imgH = (imgProps.height * availW) / imgProps.width;
      let heightLeft = imgH;
      let position = 28;
      pdf.addImage(imgData, "PNG", margin, position, availW, imgH, undefined, "FAST");
      heightLeft -= pageHeight - position;
      while (heightLeft > 0) {
        pdf.addPage();
        position = margin - (imgH - heightLeft);
        pdf.addImage(imgData, "PNG", margin, position, availW, imgH, undefined, "FAST");
        heightLeft -= pageHeight;
      }
      const safeName = (companyName || "fabrica").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      pdf.save(`painel_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF gerado com sucesso");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar PDF: " + (e.message || ""));
    } finally {
      setExportingPdf(false);
    }
  };

  // ----- Metas de Produção -----
  const openProdGoalDlg = (g = null) => {
    const now = new Date();
    if (g) {
      setProdGoalDlg({ open: true, id: g.id, data: {
        year: g.year, month: g.month || "", target_quantity: g.target_quantity, product_id: g.product_id, notes: g.notes || "",
      }});
    } else {
      setProdGoalDlg({ open: true, id: null, data: {
        year: now.getFullYear(), month: now.getMonth() + 1, target_quantity: "", product_id: products[0]?.id || "", notes: "",
      }});
    }
  };
  const saveProdGoal = async () => {
    const d = prodGoalDlg.data;
    if (!d.product_id || !parseFloat(d.target_quantity)) { toast.error("Produto e meta (positiva) são obrigatórios"); return; }
    const body = {
      year: parseInt(d.year), month: d.month ? parseInt(d.month) : null,
      target_quantity: parseFloat(d.target_quantity), product_id: d.product_id,
      notes: d.notes || null,
    };
    try {
      if (prodGoalDlg.id) {
        await axios.put(`${API}/companies/${companyId}/factory/production-goals/${prodGoalDlg.id}`, body);
      } else {
        await axios.post(`${API}/companies/${companyId}/factory/production-goals`, body);
      }
      toast.success("Meta de produção salva");
      setProdGoalDlg({ open: false, data: null, id: null });
      const r = await axios.get(`${API}/companies/${companyId}/factory/production-goals`);
      setProdGoals(r.data || []);
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const deleteProdGoal = async (id) => {
    if (!window.confirm("Excluir esta meta de produção?")) return;
    await axios.delete(`${API}/companies/${companyId}/factory/production-goals/${id}`);
    toast.success("Meta excluída");
    const r = await axios.get(`${API}/companies/${companyId}/factory/production-goals`);
    setProdGoals(r.data || []);
  };

  const openGoalDlg = (g = null) => {
    const now = new Date();
    if (g) {
      setGoalDlg({ open: true, id: g.id, data: {
        year: g.year, month: g.month || "", target_revenue: g.target_revenue,
        target_sales_count: g.target_sales_count || "",
        seller_id: g.seller_id || "", product_id: g.product_id || "", notes: g.notes || "",
      }});
    } else {
      setGoalDlg({ open: true, id: null, data: {
        year: now.getFullYear(), month: now.getMonth() + 1, target_revenue: "",
        target_sales_count: "", seller_id: "", product_id: "", notes: "",
      }});
    }
  };
  const saveGoal = async () => {
    const d = goalDlg.data;
    if (!d.year || !d.target_revenue || parseFloat(d.target_revenue) <= 0) { toast.error("Ano e meta (positiva) são obrigatórios"); return; }
    const body = {
      year: parseInt(d.year),
      month: d.month ? parseInt(d.month) : null,
      target_revenue: parseFloat(d.target_revenue),
      target_sales_count: d.target_sales_count ? parseInt(d.target_sales_count) : null,
      seller_id: d.seller_id || null,
      product_id: d.product_id || null,
      notes: d.notes || null,
    };
    try {
      if (goalDlg.id) {
        await axios.put(`${API}/companies/${companyId}/factory/sales-goals/${goalDlg.id}`, body);
      } else {
        await axios.post(`${API}/companies/${companyId}/factory/sales-goals`, body);
      }
      toast.success("Meta salva");
      setGoalDlg({ open: false, data: null, id: null });
      const r = await axios.get(`${API}/companies/${companyId}/factory/sales-goals`);
      setGoals(r.data || []);
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const deleteGoal = async (id) => {
    if (!window.confirm("Excluir esta meta?")) return;
    await axios.delete(`${API}/companies/${companyId}/factory/sales-goals/${id}`);
    toast.success("Meta excluída");
    const r = await axios.get(`${API}/companies/${companyId}/factory/sales-goals`);
    setGoals(r.data || []);
  };

  // ----- Matérias-Primas -----
  const saveMat = async () => {
    const d = matDlg.data;
    if (!d?.name) { toast.error("Nome é obrigatório"); return; }
    const body = {
      name: d.name,
      description: d.description || null,
      unit: d.unit || "kg",
      unit_cost: parseFloat(d.unit_cost) || 0,
      quantity: parseFloat(d.quantity) || 0,
      supplier_id: d.supplier_id || null,
    };
    try {
      if (matDlg.id) {
        await axios.put(`${API}/companies/${companyId}/factory/raw-materials/${matDlg.id}`, body);
      } else {
        await axios.post(`${API}/companies/${companyId}/factory/raw-materials`, body);
      }
      toast.success("Matéria-prima salva");
      setMatDlg({ open: false, data: null, id: null });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const delMat = async (id) => {
    if (!window.confirm("Excluir esta matéria-prima?")) return;
    await axios.delete(`${API}/companies/${companyId}/factory/raw-materials/${id}`);
    toast.success("Excluída");
    fetchAll();
  };

  // ----- Entrada / Compra de MP -----
  const openEntryDlg = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEntryDlg({
      open: true,
      data: {
        supplier_id: "",
        items: [{ raw_material_id: "", quantity: "", unit_cost: "", update_unit_cost: true }],
        payment_method: "avista",
        bank_account_id: bankAccounts.find(b => b.is_default)?.id || bankAccounts[0]?.id || "",
        first_due_date: today,
        installments_count: 1,
        installments: [{ installment_number: 1, due_date: todayPlusDays(30), amount: "", boleto_url: "", boleto_filename: "" }],
        invoice_number: "",
        notes: "",
      },
    });
  };
  const entryUpdateItem = (idx, key, value) => {
    const items = [...entryDlg.data.items];
    items[idx] = { ...items[idx], [key]: value };
    // Auto-preenche unit_cost quando seleciona MP
    if (key === "raw_material_id") {
      const mat = materials.find(m => m.id === value);
      if (mat && !items[idx].unit_cost) items[idx].unit_cost = mat.unit_cost;
    }
    setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items } });
  };
  const entryAddItem = () => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items: [...entryDlg.data.items, { raw_material_id: "", quantity: "", unit_cost: "", update_unit_cost: true }] } });
  const entryRemoveItem = (idx) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, items: entryDlg.data.items.filter((_, i) => i !== idx) } });

  const entryTotal = useMemo(() => {
    if (!entryDlg.data) return 0;
    return entryDlg.data.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0), 0);
  }, [entryDlg.data]);

  const entrySetInstallmentsCount = (n) => {
    n = parseInt(n) || 1;
    const existing = entryDlg.data.installments || [];
    const base = parseFloat(entryTotal.toFixed(2)) / n;
    const insts = Array.from({ length: n }).map((_, i) => existing[i] || {
      installment_number: i + 1,
      due_date: todayPlusDays(30 * (i + 1)),
      amount: base.toFixed(2),
      boleto_url: "",
      boleto_filename: "",
    });
    // Renumera + reajusta valores se vazios
    insts.forEach((it, i) => { it.installment_number = i + 1; if (!it.amount) it.amount = base.toFixed(2); });
    setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, installments_count: n, installments: insts } });
  };

  const entryUpdateInstallment = (idx, key, value) => {
    const insts = [...entryDlg.data.installments];
    insts[idx] = { ...insts[idx], [key]: value };
    setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, installments: insts } });
  };

  const entryUploadBoleto = async (idx, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/companies/${companyId}/factory/upload-boleto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      entryUpdateInstallment(idx, "boleto_url", r.data.url);
      entryUpdateInstallment(idx, "boleto_filename", r.data.filename);
      toast.success(`Boleto parcela ${idx + 1} anexado`);
    } catch (e) {
      toast.error("Erro ao enviar boleto");
    }
  };

  const submitEntry = async () => {
    const d = entryDlg.data;
    if (!d.supplier_id) { toast.error("Selecione o fornecedor"); return; }
    const items = d.items.filter(it => it.raw_material_id && parseFloat(it.quantity) > 0 && parseFloat(it.unit_cost) > 0);
    if (items.length === 0) { toast.error("Adicione pelo menos um item válido"); return; }
    if (d.payment_method === "avista" && !d.bank_account_id) { toast.error("Selecione a conta bancária para pagamento à vista"); return; }
    if (d.payment_method === "aprazo") {
      const sum = (d.installments || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
      if (Math.abs(sum - entryTotal) > 0.05) { toast.error(`Soma das parcelas (R$ ${sum.toFixed(2)}) difere do total (R$ ${entryTotal.toFixed(2)})`); return; }
    }
    try {
      await axios.post(`${API}/companies/${companyId}/factory/raw-material-entries`, {
        supplier_id: d.supplier_id,
        items: items.map(it => ({
          raw_material_id: it.raw_material_id,
          quantity: parseFloat(it.quantity),
          unit_cost: parseFloat(it.unit_cost),
          update_unit_cost: !!it.update_unit_cost,
        })),
        payment_method: d.payment_method,
        bank_account_id: d.payment_method === "avista" ? d.bank_account_id : null,
        first_due_date: d.payment_method === "avista" ? d.first_due_date : null,
        installments: d.payment_method === "aprazo"
          ? d.installments.map(i => ({
              installment_number: i.installment_number,
              due_date: i.due_date,
              amount: parseFloat(i.amount),
              boleto_url: i.boleto_url || null,
              boleto_filename: i.boleto_filename || null,
            }))
          : [],
        invoice_number: d.invoice_number || null,
        notes: d.notes || null,
      });
      toast.success("Entrada registrada - estoque atualizado e Contas a Pagar geradas");
      setEntryDlg({ open: false, data: null });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  // ----- Produtos -----
  const openProdDlg = (p = null) => {
    if (p) {
      setProdDlg({
        open: true, id: p.id,
        data: {
          ...EMPTY_PROD, ...p,
          recipe: (p.recipe && p.recipe.length ? p.recipe : [{ raw_material_id: "", quantity: "" }]),
        }
      });
    } else {
      setProdDlg({ open: true, id: null, data: { ...EMPTY_PROD } });
    }
  };

  const saveProd = async () => {
    const d = prodDlg.data;
    if (!d?.name) { toast.error("Nome é obrigatório"); return; }
    const body = {
      name: d.name,
      description: d.description || null,
      unit: d.unit || "saco",
      cost_mode: d.cost_mode || "recipe",
      manual_cost: parseFloat(d.manual_cost) || 0,
      recipe: (d.recipe || []).filter(r => r.raw_material_id && parseFloat(r.quantity) > 0)
                              .map(r => ({ raw_material_id: r.raw_material_id, quantity: parseFloat(r.quantity) })),
      tax_percent: parseFloat(d.tax_percent) || 0,
      overhead_cost: parseFloat(d.overhead_cost) || 0,
      profit_margin_percent: parseFloat(d.profit_margin_percent) || 0,
      price_wholesale: parseFloat(d.price_wholesale) || 0,
      price_retail: parseFloat(d.price_retail) || 0,
      price_special: parseFloat(d.price_special) || 0,
      barcode: (d.barcode || "").trim() || null,
    };
    try {
      if (prodDlg.id) {
        await axios.put(`${API}/companies/${companyId}/factory/products/${prodDlg.id}`, body);
      } else {
        await axios.post(`${API}/companies/${companyId}/factory/products`, body);
      }
      toast.success("Produto salvo");
      setProdDlg({ open: false, data: null, id: null });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const delProd = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    await axios.delete(`${API}/companies/${companyId}/factory/products/${id}`);
    toast.success("Excluído");
    fetchAll();
  };

  const addRecipeItem = () => {
    const d = { ...prodDlg.data, recipe: [...(prodDlg.data.recipe || []), { raw_material_id: "", quantity: "" }] };
    setProdDlg({ ...prodDlg, data: d });
  };
  const removeRecipeItem = (idx) => {
    const d = { ...prodDlg.data, recipe: prodDlg.data.recipe.filter((_, i) => i !== idx) };
    setProdDlg({ ...prodDlg, data: d });
  };
  const updateRecipeItem = (idx, key, value) => {
    const r = [...prodDlg.data.recipe];
    r[idx] = { ...r[idx], [key]: value };
    setProdDlg({ ...prodDlg, data: { ...prodDlg.data, recipe: r } });
  };

  // Preview custo em tempo real no dialog
  const previewCost = useMemo(() => {
    const d = prodDlg.data;
    if (!d) return { base: 0, unit_cost: 0, suggested: 0 };
    let base = 0;
    if (d.cost_mode === "manual") {
      base = parseFloat(d.manual_cost) || 0;
    } else {
      for (const r of (d.recipe || [])) {
        const mat = materials.find(m => m.id === r.raw_material_id);
        if (mat) base += (parseFloat(r.quantity) || 0) * (parseFloat(mat.unit_cost) || 0);
      }
    }
    const overhead = parseFloat(d.overhead_cost) || 0;
    const tax = parseFloat(d.tax_percent) || 0;
    const margin = parseFloat(d.profit_margin_percent) || 0;
    const unit_cost = (base + overhead) * (1 + tax / 100);
    const suggested = unit_cost * (1 + margin / 100);
    return { base, unit_cost, suggested };
  }, [prodDlg.data, materials]);

  // ----- Produção -----
  const confirmProduction = async () => {
    const d = productionDlg;
    const qty = parseFloat(d.quantity);
    if (!d.product_id || !qty) { toast.error("Produto e quantidade obrigatórios"); return; }
    if (!d.is_adjustment && qty <= 0) { toast.error("Quantidade de produção deve ser positiva"); return; }
    if (d.is_adjustment && qty < 0 && !(d.notes || "").trim()) {
      toast.error("Informe o motivo da baixa em Observações (ex: quebra, perda, erro)");
      return;
    }
    try {
      await axios.post(`${API}/companies/${companyId}/factory/production`, {
        product_id: d.product_id, quantity: qty, notes: d.notes || null,
        is_adjustment: !!d.is_adjustment,
      });
      toast.success(d.is_adjustment
        ? (qty < 0 ? `Baixa de ${Math.abs(qty)} un registrada` : "Ajuste positivo registrado (sem consumo de MP)")
        : "Produção registrada");
      setProductionDlg({ open: false, product_id: "", quantity: "", is_adjustment: false });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  // ----- Vendas -----
  const openSaleDlg = () => {
    setSaleDlg({ open: true, data: { items: [{ ...EMPTY_SALE_ITEM }], client_id: "", client_name_manual: "", discount: "", payment_method: "dinheiro", installments_count: 1, first_due_date: todayPlusDays(30), seller_id: "", seller_name_manual: "" } });
  };
  const openEditSaleDlg = (sale) => {
    const lastEdit = sale.updated_by_user_name && sale.updated_at
      ? { by: sale.updated_by_user_name, at: fmtDateTimeBR(sale.updated_at) }
      : null;
    setSaleDlg({
      open: true,
      editing_id: sale.id,
      editing_aprazo: sale.payment_method === "aprazo",
      last_edit: lastEdit,
      data: {
        items: (sale.items || []).map(it => ({
          product_id: it.product_id,
          quantity: String(it.quantity),
          price_type: it.price_type || "varejo",
          unit_price: String(it.unit_price),
          combo_id: it.combo_id || null, combo_name: it.combo_name || null, combo_qty: it.combo_qty || null,
        })),
        client_id: sale.client_id || "",
        client_name_manual: sale.client_name || "",
        discount: String(sale.discount || ""),
        freight: String(sale.freight || ""),
        payment_method: sale.payment_method || "dinheiro",
        installments_count: sale.installments_count || 1,
        first_due_date: todayPlusDays(30),
        seller_id: sale.seller_id || "",
        seller_name_manual: sale.seller_name || "",
        notes: sale.notes || "",
        bank_account_id: sale.bank_account_id || "",
      }
    });
  };
  const addSaleItem = () => {
    setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items: [...saleDlg.data.items, { ...EMPTY_SALE_ITEM }] } });
  };
  const removeSaleItem = (idx) => {
    setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items: saleDlg.data.items.filter((_, i) => i !== idx) } });
  };
  const updateSaleItem = (idx, key, value) => {
    const items = [...saleDlg.data.items];
    items[idx] = { ...items[idx], [key]: value };
    // Auto-preenche preço quando muda produto ou tipo
    if (key === "product_id" || key === "price_type") {
      const p = products.find(x => x.id === items[idx].product_id);
      if (p) {
        const pt = items[idx].price_type;
        const auto = pt === "atacado" ? p.price_wholesale : pt === "especial" ? p.price_special : p.price_retail;
        if (auto) items[idx].unit_price = auto;
      }
    }
    setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, items } });
  };
  const saleTotal = useMemo(() => {
    if (!saleDlg.data) return 0;
    const sub = saleDlg.data.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
    return Math.max(sub - (parseFloat(saleDlg.data.discount) || 0), 0) + (parseFloat(saleDlg.data.freight) || 0);
  }, [saleDlg.data]);

  const submitSale = async () => {
    const d = saleDlg.data;
    const valid = (d.items || []).filter(it => it.product_id && parseFloat(it.quantity) > 0 && parseFloat(it.unit_price) >= 0);
    if (valid.length === 0) { toast.error("Adicione pelo menos um item válido"); return; }
    if (!saleDlg.editing_id && d.payment_method === "aprazo" && (!parseInt(d.installments_count) || !d.first_due_date)) {
      toast.error("Para venda a prazo, informe o número de parcelas e a data do 1º vencimento");
      return;
    }
    if (saleDlg.editing_id && saleDlg.editing_aprazo && (!parseInt(d.installments_count) || !d.first_due_date)) {
      toast.error("Para editar venda a prazo, informe parcelas e data do 1º vencimento (parcelas serão regeradas)");
      return;
    }
    // Validação admin trocando p/ aprazo
    if (saleDlg.editing_id && d.payment_method === "aprazo" && (!parseInt(d.installments_count) || !d.first_due_date)) {
      toast.error("Para A Prazo, informe parcelas e data do 1º vencimento");
      return;
    }
    // Validação admin trocando p/ pix/cartão
    if (saleDlg.editing_id && ["pix", "cartao"].includes(d.payment_method) && !d.bank_account_id) {
      toast.error("Selecione a conta bancária que recebe o pagamento");
      return;
    }
    try {
      if (saleDlg.editing_id) {
        // Admin edit: endpoint PUT (pode trocar forma de pagamento)
        const putBody = {
          items: valid.map(it => ({
            product_id: it.product_id,
            quantity: parseFloat(it.quantity),
            price_type: it.price_type,
            unit_price: parseFloat(it.unit_price),
            combo_id: it.combo_id || null, combo_name: it.combo_name || null, combo_qty: it.combo_qty || null,
          })),
          client_id: d.client_id || null,
          client_name_manual: d.client_name_manual || null,
          discount: parseFloat(d.discount) || 0,
          freight: parseFloat(d.freight) || 0,
          notes: d.notes || null,
          seller_id: d.seller_id || null,
          seller_name_manual: d.seller_name_manual || null,
          payment_method: d.payment_method || null,
          bank_account_id: ["pix", "cartao"].includes(d.payment_method) ? (d.bank_account_id || null) : null,
        };
        if (d.payment_method === "aprazo") {
          putBody.installments_count = parseInt(d.installments_count) || 1;
          putBody.first_due_date = d.first_due_date || null;
        }
        await axios.put(`${API}/companies/${companyId}/factory/sales/${saleDlg.editing_id}`, putBody);
        toast.success("Venda atualizada" + (d.payment_method === "aprazo" ? " · parcelas regeradas" : ""));
      } else {
        await axios.post(`${API}/companies/${companyId}/factory/sales`, {
          items: valid.map(it => ({
            product_id: it.product_id,
            quantity: parseFloat(it.quantity),
            price_type: it.price_type,
            unit_price: parseFloat(it.unit_price),
          })),
          client_id: d.client_id || null,
          client_name_manual: d.client_name_manual || null,
          discount: parseFloat(d.discount) || 0,
          freight: parseFloat(d.freight) || 0,
          notes: d.notes || null,
          payment_method: d.payment_method || "dinheiro",
          installments_count: parseInt(d.installments_count) || 1,
          first_due_date: d.payment_method === "aprazo" ? (d.first_due_date || null) : null,
          seller_id: d.seller_id || null,
          seller_name_manual: d.seller_name_manual || null,
          bank_account_id: ["pix", "cartao"].includes(d.payment_method) ? (d.bank_account_id || null) : null,
        });
        toast.success("Venda registrada");
      }
      setSaleDlg({ open: false, data: null });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  // Parcelas: modal e pagamento (usa coleção principal `installments` via endpoint padrão)
  const [instDlg, setInstDlg] = useState({ open: false, sale: null, items: [] });
  const [payDlg, setPayDlg] = useState({ open: false, inst: null, bank_account_id: "" });

  const openInstallments = async (sale) => {
    const r = await axios.get(`${API}/companies/${companyId}/factory/sales/${sale.id}/installments`);
    setInstDlg({ open: true, sale, items: r.data || [] });
  };
  const askPayInstallment = (inst) => {
    const defaultBank = bankAccounts.find(b => b.is_default) || bankAccounts[0];
    setPayDlg({ open: true, inst, bank_account_id: defaultBank?.id || "" });
  };
  const confirmPayInstallment = async () => {
    if (!payDlg.bank_account_id) { toast.error("Selecione a conta bancária que receberá o valor"); return; }
    try {
      await axios.patch(`${API}/companies/${companyId}/installments/${payDlg.inst.id}/pay`, {
        bank_account_id: payDlg.bank_account_id,
      });
      toast.success("Parcela paga - entrada registrada em Transações");
      const r = await axios.get(`${API}/companies/${companyId}/factory/sales/${instDlg.sale.id}/installments`);
      setInstDlg({ ...instDlg, items: r.data || [] });
      setPayDlg({ open: false, inst: null, bank_account_id: "" });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const revertInstallmentPayment = async (inst) => {
    if (!window.confirm(`Estornar o pagamento da parcela ${inst.installment_number}? O valor será debitado da conta bancária e a parcela voltará para pendente.`)) return;
    try {
      await axios.post(`${API}/companies/${companyId}/installments/${inst.id}/revert`);
      toast.success(`Parcela ${inst.installment_number} estornada`);
      const r = await axios.get(`${API}/companies/${companyId}/factory/sales/${instDlg.sale.id}/installments`);
      setInstDlg({ ...instDlg, items: r.data || [] });
      fetchAll();
    } catch (e) {
      toast.error("Erro ao estornar: " + (e.response?.data?.detail || ""));
    }
  };

  const openCreditDlg = (client) => {
    setCreditDlg({
      open: true,
      client,
      credit_limit: client.credit_limit || "",
      max_days: client.max_days || 20,
      max_installments: client.max_installments || 1,
      notes: client.credit_notes || "",
    });
  };
  const saveCredit = async () => {
    if (!creditDlg.client) return;
    try {
      await axios.post(`${API}/companies/${companyId}/factory/clients-credit/${creditDlg.client.id}/approve`, {
        credit_limit: parseFloat(creditDlg.credit_limit) || 0,
        max_days: parseInt(creditDlg.max_days),
        max_installments: parseInt(creditDlg.max_installments),
        notes: creditDlg.notes || null,
      });
      toast.success("Crédito aprovado");
      setCreditDlg({ open: false, client: null, credit_limit: "", max_days: 20, max_installments: 1, notes: "" });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const revokeCredit = async (client) => {
    if (!window.confirm(`Revogar crédito de ${client.name}?`)) return;
    try {
      await axios.post(`${API}/companies/${companyId}/factory/clients-credit/${client.id}/revoke`);
      toast.success("Crédito revogado");
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  // ============== RENDER ==============
  if (driverOnly) {
    return (
      <div className="space-y-4 factory-module" data-testid="factory-module-driver">
        <style>{`.factory-module, .factory-module * { color: #000000 !important; } .factory-module .app-badge, .factory-module .app-badge * { color: #ffffff !important; } .factory-module .app-badge-neg, .factory-module .app-badge-neg * { color: #b91c1c !important; }`}</style>
        <h2 className="text-xl md:text-2xl font-extrabold flex items-center gap-2"><Truck className="h-6 w-6" />Minhas Entregas</h2>
        <DeliveriesTab companyId={companyId} companyName={companyName} />
      </div>
    );
  }
  return (
    <div className="space-y-4 factory-module" data-testid="factory-module">
      <style>{`.factory-module, .factory-module * { color: #000000 !important; } .factory-module .app-badge, .factory-module .app-badge * { color: #ffffff !important; } .factory-module .app-badge-neg, .factory-module .app-badge-neg * { color: #b91c1c !important; }`}</style>
      <div className="flex items-center gap-2">
        <Factory className="h-6 w-6 text-indigo-700" />
        <h2 className="text-xl md:text-2xl font-extrabold">Fábrica de Argamassa</h2>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="inline-flex w-full mb-3 h-auto overflow-x-auto whitespace-nowrap">
          <TabsTrigger value="materias" data-testid="factory-tab-materias"><Package className="h-4 w-4 mr-1" />Matérias-Primas</TabsTrigger>
          <TabsTrigger value="produtos" data-testid="factory-tab-produtos"><FlaskConical className="h-4 w-4 mr-1" />Produtos</TabsTrigger>
          <TabsTrigger value="producao" data-testid="factory-tab-producao"><Factory className="h-4 w-4 mr-1" />Produção</TabsTrigger>
          <TabsTrigger value="vendas" data-testid="factory-tab-vendas"><ShoppingCart className="h-4 w-4 mr-1" />Vendas</TabsTrigger>
          <TabsTrigger value="caixa" data-testid="factory-tab-caixa"><ReceiptIcon className="h-4 w-4 mr-1" />Caixa</TabsTrigger>
          <TabsTrigger value="credito" data-testid="factory-tab-credito"><UserCheck className="h-4 w-4 mr-1" />Clientes / Crédito</TabsTrigger>
          {canDeliveries && <TabsTrigger value="entregas" data-testid="factory-tab-entregas"><Truck className="h-4 w-4 mr-1" />Entregas</TabsTrigger>}
          <TabsTrigger value="catalogo" data-testid="factory-tab-catalogo"><BookOpen className="h-4 w-4 mr-1" />Catálogo</TabsTrigger>
          <TabsTrigger value="combos" data-testid="factory-tab-combos"><Gift className="h-4 w-4 mr-1" />Combos</TabsTrigger>
          <TabsTrigger value="metas" data-testid="factory-tab-metas"><TrendingUp className="h-4 w-4 mr-1" />Metas</TabsTrigger>
          {canViewReports && <TabsTrigger value="relatorio" data-testid="factory-tab-relatorio"><BarChart3 className="h-4 w-4 mr-1" />Relatório</TabsTrigger>}
          {canViewReports && <TabsTrigger value="admin" data-testid="factory-tab-admin"><Shield className="h-4 w-4 mr-1" />Administrador</TabsTrigger>}
        </TabsList>

        {/* ----- MATERIAS ----- */}
        <TabsContent value="materias" className="space-y-3">
          <div className="flex justify-end gap-2 flex-wrap">
            <Button className="bg-emerald-500 hover:bg-emerald-600" disabled={!canManageMat}
              onClick={openEntryDlg} data-testid="factory-new-entry">
              <Plus className="h-4 w-4 mr-1" />Nova Entrada (Compra)
            </Button>
            <Button className="bg-blue-500 hover:bg-blue-600" disabled={!canManageMat}
              onClick={() => setMatDlg({ open: true, id: null, data: { ...EMPTY_MAT } })}
              data-testid="factory-new-material">
              <Plus className="h-4 w-4 mr-1" />Nova Matéria-Prima
            </Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Nome</th>
                  <th className="text-left p-2">Un.</th>
                  <th className="text-right p-2">Custo Un.</th>
                  <th className="text-right p-2">Estoque</th>
                  <th className="text-left p-2">Fornecedor</th>
                  <th className="text-center p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {materials.map(m => (
                  <tr key={m.id} className="border-t hover:bg-gray-50">
                    <td className="p-2 font-medium">{m.name}</td>
                    <td className="p-2">{m.unit}</td>
                    <td className="p-2 text-right">{fmt(m.unit_cost)}</td>
                    <td className="p-2 text-right font-bold">{Number(m.quantity || 0).toLocaleString("pt-BR")}</td>
                    <td className="p-2 text-xs">{m.supplier_name || "—"}</td>
                    <td className="p-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" disabled={!canManageMat}
                          onClick={() => setMatDlg({ open: true, id: m.id, data: { ...m, supplier_id: m.supplier_id || "" } })}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" disabled={!canManageMat}
                          onClick={() => delMat(m.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {materials.length === 0 && <tr><td colSpan={6} className="p-6 text-center">Nenhuma matéria-prima cadastrada</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ----- PRODUTOS ----- */}
        <TabsContent value="produtos" className="space-y-3">
          <div className="flex justify-end">
            <Button className="bg-indigo-500 hover:bg-indigo-600" disabled={!canManageProd}
              onClick={() => openProdDlg()} data-testid="factory-new-product">
              <Plus className="h-4 w-4 mr-1" />Novo Produto
            </Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2 w-14">Cód.</th>
                  <th className="text-left p-2">Produto</th>
                  <th className="text-left p-2">Un.</th>
                  <th className="text-right p-2">Custo Un. (c/ imp.)</th>
                  <th className="text-right p-2">Sug.</th>
                  <th className="text-right p-2">Atacado</th>
                  <th className="text-right p-2">Varejo</th>
                  <th className="text-right p-2">Especial</th>
                  <th className="text-right p-2">Estoque</th>
                  <th className="text-center p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-t hover:bg-gray-50">
                    <td className="p-2 font-mono text-xs font-bold text-emerald-700">{p.code || "—"}</td>
                    <td className="p-2 font-medium">{p.name}</td>
                    <td className="p-2">{p.unit}</td>
                    <td className="p-2 text-right">{fmt(p.computed_unit_cost)}</td>
                    <td className="p-2 text-right text-blue-700">{fmt(p.suggested_price)}</td>
                    <td className="p-2 text-right">{fmt(p.price_wholesale)}</td>
                    <td className="p-2 text-right">{fmt(p.price_retail)}</td>
                    <td className="p-2 text-right">{fmt(p.price_special)}</td>
                    <td className="p-2 text-right font-bold">{Number(p.quantity || 0).toLocaleString("pt-BR")}</td>
                    <td className="p-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" disabled={!canManageProd} onClick={() => openProdDlg(p)}><Edit className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600" disabled={!canManageProd} onClick={() => delProd(p.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan={10} className="p-6 text-center">Nenhum produto cadastrado</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ----- PRODUCAO ----- */}
        <TabsContent value="producao" className="space-y-3">
          {/* Metas de Produção */}
          <div className="border rounded-lg p-3 bg-amber-50 space-y-2">
            <div className="flex justify-between items-center">
              <h4 className="font-bold text-sm">🎯 Metas de Produção</h4>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => openProdGoalDlg()} data-testid="new-prod-goal">
                <Plus className="h-3 w-3 mr-1" />Nova Meta
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {prodGoals.map(g => {
                const pct = Math.min(g.progress_percent || 0, 100);
                const bar = pct >= 100 ? "bg-green-500" : pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
                return (
                  <div key={g.id} className={`border rounded p-2 bg-white ${pct >= 100 ? "border-green-500 border-2" : ""}`}>
                    <div className="flex justify-between items-start text-xs">
                      <div>
                        <p className="font-bold">{g.product_name}</p>
                        <p>{g.month ? `${String(g.month).padStart(2, "0")}/${g.year}` : `Ano ${g.year}`}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => openProdGoalDlg(g)}><Edit className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" className="h-6 w-6 p-0 text-red-600" onClick={() => deleteProdGoal(g.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span>Meta: <b>{g.target_quantity} {g.product_unit}</b></span>
                      <span>{pct.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                      <div className={`h-2 ${bar}`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <p className="text-xs mt-1">Produzido: <b className="text-green-700">{g.produced}</b> · Falta: <b className="text-red-700">{g.remaining}</b></p>
                  </div>
                );
              })}
              {prodGoals.length === 0 && <p className="col-span-3 text-xs text-center py-2">Nenhuma meta cadastrada</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" className="text-amber-800 border-amber-300" disabled={!canProduce}
              onClick={() => setProductionDlg({ open: true, product_id: "", quantity: "", notes: "", is_adjustment: true })}
              data-testid="factory-initial-stock">
              <Plus className="h-4 w-4 mr-1" />Estoque Inicial / Ajuste
            </Button>
            <Button className="bg-amber-500 hover:bg-amber-600" disabled={!canProduce}
              onClick={() => setProductionDlg({ open: true, product_id: "", quantity: "", notes: "", is_adjustment: false })}
              data-testid="factory-new-production">
              <Plus className="h-4 w-4 mr-1" />Registrar Produção
            </Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Data</th>
                  <th className="text-center p-2">Tipo</th>
                  <th className="text-left p-2">Produto</th>
                  <th className="text-right p-2">Qtd Produzida</th>
                  <th className="text-left p-2">Matérias-Primas Consumidas</th>
                  <th className="text-left p-2">Por</th>
                </tr>
              </thead>
              <tbody>
                {productions.map(pr => (
                  <tr key={pr.id} className="border-t">
                    <td className="p-2 text-xs">{fmtDateTimeBR(pr.created_at)}</td>
                    <td className="p-2 text-center">
                      {pr.is_adjustment
                        ? <Badge className="bg-amber-500 text-xs">Ajuste</Badge>
                        : <Badge className="bg-blue-600 text-xs">Produção</Badge>}
                    </td>
                    <td className="p-2 font-medium">{pr.product_name}</td>
                    <td className="p-2 text-right font-bold">{pr.quantity}</td>
                    <td className="p-2 text-xs">
                      {pr.is_adjustment ? <span className="text-gray-500 italic">— (ajuste, sem consumo)</span> :
                        ((pr.consumed_materials || []).map(c => `${c.raw_material_name}: ${c.quantity} ${c.unit || ""}`).join(" · ") || "—")}
                    </td>
                    <td className="p-2 text-xs">{pr.created_by_user_name || "—"}</td>
                  </tr>
                ))}
                {productions.length === 0 && <tr><td colSpan={6} className="p-6 text-center">Nenhum lançamento</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ----- VENDAS ----- */}
        <TabsContent value="vendas" className="space-y-3">
          <div className="flex justify-end gap-2">
            <Button className="bg-emerald-500 hover:bg-emerald-600" disabled={!canSell || !canUsePos} onClick={() => setPosOpen(true)} data-testid="factory-open-pos" title={!canUsePos ? "Sem permissão para usar o Frente de Caixa" : ""}>
              <ShoppingCart className="h-4 w-4 mr-1" />Frente de Caixa
            </Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Data</th>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-left p-2">Vendedor</th>
                  <th className="text-center p-2">Pgto</th>
                  <th className="text-right p-2">Itens</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-right p-2">Saldo</th>
                  <th className="text-right p-2">Lucro</th>
                  <th className="text-right p-2">Comissão</th>
                  <th className="text-center p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sales.map(s => (
                  <tr key={s.id} className="border-t hover:bg-gray-50">
                    <td className="p-2 font-mono">#{String(s.sale_number).padStart(4, "0")}</td>
                    <td className="p-2 text-xs">{fmtDateTimeBR(s.created_at)}</td>
                    <td className="p-2">
                      {s.client_name || "Consumidor"}
                      {s.delivery_status && <Badge className="ml-1 text-[9px] px-1 py-0" data-testid={`sale-delivery-${s.id}`}><Truck className="h-3 w-3 mr-0.5" />{{ agendada: "Entrega agendada", em_rota: "Em rota", entregue: "Entregue", cancelada: "Entrega cancelada" }[s.delivery_status]}</Badge>}
                    </td>
                    <td className="p-2 text-xs">{s.seller_name || "—"}{s.seller_role ? <span className="text-gray-500"> · {s.seller_role}</span> : null}</td>
                    <td className="p-2 text-center">
                      <Badge variant="outline" className="text-xs">
                        {s.is_pix_aprazo ? "PIX a Prazo 4d" : (PAYMENT_LABEL[s.payment_method] || "Dinheiro")}
                        {s.payment_method === "aprazo" && !s.is_pix_aprazo && s.installments_count > 1 ? ` ${s.installments_count}x` : ""}
                      </Badge>
                    </td>
                    <td className="p-2 text-right">{(s.items || []).length}</td>
                    <td className="p-2 text-right font-bold">{fmt(s.total)}</td>
                    <td className="p-2 text-right">
                      {s.payment_method === "aprazo" ? (
                        <span className={s.payment_status === "pago" ? "text-green-600" : (s.payment_status === "parcial" ? "text-amber-600" : "text-red-600")}>
                          {fmt(s.balance_due || 0)}
                        </span>
                      ) : <span className="text-green-600">{fmt(0)}</span>}
                    </td>
                    <td className="p-2 text-right font-bold text-green-700">{fmt(s.profit)}</td>
                    <td className="p-2 text-right text-emerald-700 font-bold">{fmt(s.commission_amount || 0)}</td>
                    <td className="p-2 text-center">
                      <div className="flex gap-1 justify-center">
                        {s.payment_method === "aprazo" ? (
                          <Button size="sm" variant="outline" onClick={() => openInstallments(s)} data-testid={`view-installments-${s.id}`}>
                            Parcelas
                          </Button>
                        ) : null}
                        {isAdmin && (
                          <Button size="sm" variant="outline" onClick={() => openEditSaleDlg(s)} data-testid={`edit-sale-${s.id}`} title="Editar venda (admin)">
                            Editar
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setDupReceipt(saleToReceipt(s, companyName))} data-testid={`sale-duplicate-${s.id}`} title="2ª via do cupom">
                          <Printer className="h-3 w-3 mr-1" />2ª Via
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sales.length === 0 && <tr><td colSpan={11} className="p-6 text-center">Nenhuma venda</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ----- CLIENTES / CRÉDITO ----- */}
        <TabsContent value="credito" className="space-y-3">
          <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
            Apenas clientes com crédito <b>aprovado</b> podem comprar na modalidade <b>A Prazo</b>. Prazos permitidos: <b>20, 45 ou 60 dias</b> · Parcelas: até <b>3x</b>.
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[950px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-center p-2">Status</th>
                  <th className="text-right p-2">Limite</th>
                  <th className="text-right p-2">Saldo em aberto</th>
                  <th className="text-right p-2">Disponível</th>
                  <th className="text-center p-2">Prazo</th>
                  <th className="text-center p-2">Parcelas</th>
                  <th className="text-left p-2">Aprovado por</th>
                  <th className="text-center p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientsCredit.map(cl => (
                  <tr key={cl.id} className="border-t hover:bg-gray-50">
                    <td className="p-2 font-medium">{cl.name}</td>
                    <td className="p-2 text-center">
                      {cl.credit_status === "approved"
                        ? <Badge className="bg-green-600">Aprovado</Badge>
                        : cl.credit_status === "revoked"
                        ? <Badge className="bg-red-600">Revogado</Badge>
                        : <Badge variant="outline">Sem crédito</Badge>}
                    </td>
                    <td className="p-2 text-right">{fmt(cl.credit_limit || 0)}</td>
                    <td className="p-2 text-right text-amber-700">{fmt(cl.open_balance || 0)}</td>
                    <td className="p-2 text-right font-bold text-green-700">{fmt(cl.available_limit || 0)}</td>
                    <td className="p-2 text-center">{cl.max_days ? `${cl.max_days}d` : "—"}</td>
                    <td className="p-2 text-center">{cl.max_installments ? `${cl.max_installments}x` : "—"}</td>
                    <td className="p-2 text-xs">{cl.approved_by || "—"}</td>
                    <td className="p-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" disabled={!canApproveCredit}
                          onClick={() => openCreditDlg(cl)} data-testid={`approve-credit-${cl.id}`}>
                          {cl.credit_status === "approved" ? "Editar" : "Aprovar"}
                        </Button>
                        {cl.credit_status === "approved" && (
                          <Button size="sm" variant="outline" className="text-red-600" disabled={!canApproveCredit} onClick={() => revokeCredit(cl)}>Revogar</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {clientsCredit.length === 0 && <tr><td colSpan={9} className="p-6 text-center">Nenhum cliente cadastrado. Vá ao módulo Clientes.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ----- CAIXA (SESSIONS) ----- */}
        <TabsContent value="caixa" className="space-y-3" data-testid="factory-caixa-content">
          <div className="flex justify-between items-center">
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 flex-1 mr-2">
              Abra uma sessão de caixa antes de vender no PDV. Ao fechar, informe o dinheiro contado para conferir eventuais diferenças.
            </div>
          </div>
          {currentSession ? (
            <>
              <Card className="bg-emerald-50 border-emerald-300">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <LockOpen className="h-5 w-5 text-emerald-700" />
                        <span className="font-bold text-base">Caixa aberto · {currentSession.operator_name}</span>
                      </div>
                      <div className="text-xs">Abertura: <b>{fmtDateTimeBR(currentSession.opened_at)}</b></div>
                      <div className="text-xs">Valor inicial: <b>{fmt(currentSession.opening_amount)}</b></div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button className="bg-sky-500 hover:bg-sky-600" onClick={() => setMovDlg({ open: true, type: "suprimento", amount: "", reason: "", bank_account_id: "" })} data-testid="btn-suprimento">
                        <Plus className="h-4 w-4 mr-1" />Suprimento
                      </Button>
                      <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => setMovDlg({ open: true, type: "sangria", amount: "", reason: "", bank_account_id: "" })} data-testid="btn-sangria">
                        <Trash2 className="h-4 w-4 mr-1" />Sangria
                      </Button>
                      <Button className="bg-red-500 hover:bg-red-600" onClick={() => setCloseSessionDlg({ open: true, session: currentSession, counted: "", notes: "" })} data-testid="close-cash-btn">
                        <Lock className="h-4 w-4 mr-1" />Fechar Caixa
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Movimentações da sessão aberta */}
              {cashMovements.length > 0 && (
                <div className="border rounded p-2 bg-white">
                  <h4 className="text-xs font-bold uppercase mb-1">Movimentações do turno</h4>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr><th className="text-left p-1">Hora</th><th className="text-left p-1">Tipo</th><th className="text-right p-1">Valor</th><th className="text-left p-1">Motivo</th><th className="text-left p-1">Conta</th></tr>
                    </thead>
                    <tbody>
                      {cashMovements.map(m => (
                        <tr key={m.id} className="border-t">
                          <td className="p-1">{fmtDateTimeBR(m.created_at).split(" ")[1] || fmtDateTimeBR(m.created_at)}</td>
                          <td className="p-1">
                            <Badge className={m.type === "sangria" ? "bg-amber-600" : "bg-sky-600"}>
                              {m.type === "sangria" ? "Sangria" : "Suprimento"}
                            </Badge>
                          </td>
                          <td className={`p-1 text-right font-bold ${m.type === "sangria" ? "text-amber-700" : "text-sky-700"}`}>
                            {m.type === "sangria" ? "-" : "+"}{fmt(m.amount)}
                          </td>
                          <td className="p-1">{m.reason || "—"}</td>
                          <td className="p-1 text-gray-500">{m.transaction_id ? "→ banco" : "só caixa"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <Card className="bg-gray-50 border-gray-300">
              <CardContent className="p-4 text-center">
                <Lock className="h-6 w-6 mx-auto text-gray-500 mb-1" />
                <p className="text-sm">Nenhum caixa aberto para você. Abra o caixa a partir do botão <b>"Frente de Caixa"</b> em Vendas.</p>
              </CardContent>
            </Card>
          )}

          <h3 className="font-bold text-sm mt-3">Histórico de Sessões</h3>
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Operador</th>
                  <th className="text-left p-2">Abertura</th>
                  <th className="text-left p-2">Fechamento</th>
                  <th className="text-right p-2">Inicial</th>
                  <th className="text-right p-2">Vendas</th>
                  <th className="text-right p-2">Supr./Sang.</th>
                  <th className="text-right p-2">Dinheiro Esperado</th>
                  <th className="text-right p-2">Contado</th>
                  <th className="text-right p-2">Diferença</th>
                  <th className="text-center p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {cashSessions.map(s => (
                  <tr key={s.id} className="border-t">
                    <td className="p-2">{s.operator_name}</td>
                    <td className="p-2 text-xs">{s.opened_at ? fmtDateTimeBR(s.opened_at) : "—"}</td>
                    <td className="p-2 text-xs">{s.closed_at ? fmtDateTimeBR(s.closed_at) : "—"}</td>
                    <td className="p-2 text-right">{fmt(s.opening_amount)}</td>
                    <td className="p-2 text-right">{fmt(s.total_sales || 0)} <span className="text-gray-400 text-xs">({s.sales_count || 0})</span></td>
                    <td className="p-2 text-right text-xs">
                      {s.suprimento_total ? <span className="text-sky-700">+{fmt(s.suprimento_total)}</span> : null}
                      {s.suprimento_total && s.sangria_total ? " / " : null}
                      {s.sangria_total ? <span className="text-amber-700">-{fmt(s.sangria_total)}</span> : null}
                      {!s.suprimento_total && !s.sangria_total ? "—" : null}
                    </td>
                    <td className="p-2 text-right">{fmt(s.expected_cash || 0)}</td>
                    <td className="p-2 text-right">{s.status === "closed" ? fmt(s.counted_cash || 0) : "—"}</td>
                    <td className={`p-2 text-right font-bold ${(s.difference || 0) === 0 ? "" : (s.difference > 0 ? "text-emerald-700" : "text-red-700")}`}>
                      {s.status === "closed" ? fmt(s.difference || 0) : "—"}
                    </td>
                    <td className="p-2 text-center">
                      {s.status === "open" ? <Badge className="bg-emerald-600">Aberto</Badge> : <Badge variant="outline">Fechado</Badge>}
                    </td>
                  </tr>
                ))}
                {cashSessions.length === 0 && (
                  <tr><td colSpan={10} className="p-6 text-center text-gray-500">Sem sessões registradas</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* NFC-e Pendentes (contingência) */}
          {pendingNfce.length > 0 && (
            <div className="border-2 border-amber-400 rounded bg-amber-50 p-3 mt-3" data-testid="nfce-pending-section">
              <h3 className="font-bold text-sm text-amber-800 mb-2">⚠️ NFC-e Pendentes (contingência Focus NFe)</h3>
              <p className="text-xs text-amber-700 mb-2">Estas vendas foram salvas mas a emissão da nota falhou. Clique em Reemitir quando o Focus NFe voltar.</p>
              <table className="w-full text-sm">
                <thead className="bg-amber-100">
                  <tr>
                    <th className="text-left p-2">#</th>
                    <th className="text-left p-2">Cliente</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-left p-2">Erro</th>
                    <th className="text-center p-2">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingNfce.map(p => (
                    <tr key={p.id} className="border-t border-amber-200">
                      <td className="p-2 font-mono">#{String(p.sale_number).padStart(4, "0")}</td>
                      <td className="p-2">{p.client_name || "Consumidor"}</td>
                      <td className="p-2 text-right">{fmt(p.total)}</td>
                      <td className="p-2 text-xs text-amber-900 max-w-[280px] truncate" title={p.nfce_error}>{p.nfce_error || "—"}</td>
                      <td className="p-2 text-center">
                        <Button size="sm" className="bg-sky-500 hover:bg-sky-600" onClick={() => retryNfce(p.id)} data-testid={`retry-nfce-${p.id}`}>
                          Reemitir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ----- ENTREGAS ----- */}
        {canDeliveries && (
          <TabsContent value="entregas" className="space-y-3">
            <DeliveriesTab companyId={companyId} companyName={companyName} />
          </TabsContent>
        )}

        {/* ----- CATÁLOGO ----- */}
        <TabsContent value="catalogo" className="space-y-3">
          <CatalogTab companyId={companyId} companyName={companyName} products={products} onProductsChanged={fetchAll} />
        </TabsContent>

        {/* ----- COMBOS ----- */}
        <TabsContent value="combos" className="space-y-3">
          <CombosTab companyId={companyId} products={products} canManage={canManageProd} />
        </TabsContent>

        {/* ----- METAS DE VENDAS ----- */}
        <TabsContent value="metas" className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 flex-1 mr-2">
              Defina metas <b>mensais</b> ou <b>anuais</b> para a Fábrica. Opcionalmente filtre por vendedor ou produto.
            </div>
            <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => openGoalDlg()} data-testid="new-goal">
              <Plus className="h-4 w-4 mr-1" />Nova Meta
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {goals.filter(g => !g.client_id).map(g => {
              const pct = Math.min(g.progress_percent || 0, 100);
              const barColor = pct >= 100 ? "bg-green-500" : pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
              return (
                <Card key={g.id} className={pct >= 100 ? "border-green-500 border-2" : ""}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-base">
                          {g.month ? `${String(g.month).padStart(2, "0")}/${g.year}` : `Ano ${g.year}`}
                        </p>
                        <p className="text-xs">
                          {g.seller_name ? <span className="text-blue-700">Vendedor: {g.seller_name}</span> : null}
                          {g.product_name ? <span className="text-purple-700">Produto: {g.product_name}</span> : null}
                          {!g.seller_name && !g.product_name ? <span className="text-gray-600">Meta geral</span> : null}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openGoalDlg(g)}><Edit className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteGoal(g.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Meta: <b>{fmt(g.target_revenue)}</b></span>
                      <span>{pct.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                      <div className={`h-2 ${barColor}`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Realizado: <b className="text-green-700">{fmt(g.achieved_revenue)}</b></span>
                      <span>Falta: <b className="text-red-700">{fmt(g.remaining)}</b></span>
                    </div>
                    <p className="text-xs text-gray-600">{g.achieved_sales_count} venda(s) no período</p>
                    {g.notes && <p className="text-xs italic">{g.notes}</p>}
                  </CardContent>
                </Card>
              );
            })}
            {goals.filter(g => !g.client_id).length === 0 && (
              <div className="col-span-3 border rounded p-8 text-center">
                <p>Nenhuma meta cadastrada ainda.</p>
                <Button className="mt-3 bg-emerald-500 hover:bg-emerald-600" onClick={() => openGoalDlg()}>Criar primeira meta</Button>
              </div>
            )}
          </div>
          <ClientProductGoals
            companyId={companyId}
            goals={goals.filter(g => g.client_id)}
            clients={clients}
            products={products}
            onChanged={() => axios.get(`${API}/companies/${companyId}/factory/sales-goals`).then(r => setGoals(r.data || []))}
          />
        </TabsContent>

        {/* ----- RELATORIO ----- */}
        {canViewReports && (
          <TabsContent value="relatorio" className="space-y-3">
            <div className="flex flex-wrap gap-2 items-end border rounded p-2 bg-gray-50">
              <div>
                <label className="text-xs font-bold block">De</label>
                <Input type="date" value={reportStart} onChange={e => setReportStart(e.target.value)} data-testid="report-start" />
              </div>
              <div>
                <label className="text-xs font-bold block">Até</label>
                <Input type="date" value={reportEnd} onChange={e => setReportEnd(e.target.value)} data-testid="report-end" />
              </div>
              <Button size="sm" variant="outline" onClick={() => { setReportStart(""); setReportEnd(""); }} data-testid="report-clear">Limpar</Button>
              <Button size="sm" variant="outline" onClick={() => {
                const now = new Date();
                setReportStart(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
                setReportEnd(now.toISOString().slice(0, 10));
              }}>Mês atual</Button>
              <Button size="sm" variant="outline" onClick={() => {
                const now = new Date();
                setReportStart(new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10));
                setReportEnd(now.toISOString().slice(0, 10));
              }}>Ano atual</Button>
            </div>
            {report ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Card><CardContent className="p-4"><p className="text-xs">Vendas</p><p className="text-xl md:text-2xl font-bold text-blue-700">{report.total_sales_count}</p></CardContent></Card>
                  <Card className="bg-green-50"><CardContent className="p-4"><p className="text-xs">Receita</p><p className="text-xl md:text-2xl font-bold text-green-700">{fmt(report.total_revenue)}</p></CardContent></Card>
                  <Card className="bg-red-50"><CardContent className="p-4"><p className="text-xs">Custo</p><p className="text-xl md:text-2xl font-bold text-red-700">{fmt(report.total_cost)}</p></CardContent></Card>
                  <Card className="bg-purple-50"><CardContent className="p-4"><p className="text-xs">Lucro · Margem</p><p className="text-xl md:text-2xl font-bold text-purple-700">{fmt(report.total_profit)}</p><p className="text-xs">{report.margin_percent}%</p></CardContent></Card>
                </div>
                <Card>
                  <CardHeader><CardTitle className="text-base">Faturamento por Tipo de Preço</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(report.by_price_type || {}).map(([k, v]) => (
                        <div key={k} className="border rounded p-3 text-center">
                          <p className="text-xs uppercase">{PRICE_LABEL[k] || k}</p>
                          <p className="font-bold">{fmt(v)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Top Produtos por Lucro</CardTitle></CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm min-w-[600px]">
                        <thead className="bg-gray-100"><tr><th className="text-left p-2">Produto</th><th className="text-right p-2">Qtd</th><th className="text-right p-2">Receita</th><th className="text-right p-2">Lucro</th></tr></thead>
                        <tbody>
                          {(report.top_products || []).map(p => (
                            <tr key={p.product_id} className="border-t">
                              <td className="p-2">{p.product_name}</td>
                              <td className="p-2 text-right">{p.qty}</td>
                              <td className="p-2 text-right">{fmt(p.revenue)}</td>
                              <td className="p-2 text-right font-bold text-green-700">{fmt(p.profit)}</td>
                            </tr>
                          ))}
                          {(report.top_products || []).length === 0 && <tr><td colSpan={4} className="p-4 text-center">Sem vendas</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Top Vendedores · Comissão ({((report.commission_rate || 0.04) * 100).toFixed(0)}% sobre o total)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm min-w-[600px]">
                        <thead className="bg-gray-100"><tr>
                          <th className="text-left p-2">Vendedor</th>
                          <th className="text-right p-2">Vendas</th>
                          <th className="text-right p-2">Receita</th>
                          <th className="text-right p-2">Lucro</th>
                          <th className="text-right p-2">Comissão</th>
                        </tr></thead>
                        <tbody>
                          {(report.top_sellers || []).map(s => (
                            <tr key={s.seller_id || s.seller_name} className="border-t">
                              <td className="p-2">{s.seller_name}{s.seller_role ? <span className="text-gray-500 text-xs"> · {s.seller_role}</span> : null}</td>
                              <td className="p-2 text-right">{s.sales_count}</td>
                              <td className="p-2 text-right">{fmt(s.revenue)}</td>
                              <td className="p-2 text-right text-green-700">{fmt(s.profit)}</td>
                              <td className="p-2 text-right font-bold text-emerald-700">{fmt(s.commission)}</td>
                            </tr>
                          ))}
                          {(report.top_sellers || []).length === 0 && <tr><td colSpan={5} className="p-4 text-center">Sem vendedores</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : <p className="text-center py-4">Carregando...</p>}
          </TabsContent>
        )}

        {/* ----- ADMINISTRADOR ----- */}
        {canViewReports && (
          <TabsContent value="admin" className="space-y-3">
            <div className="flex flex-wrap gap-2 items-end border rounded p-2 bg-indigo-50">
              <div>
                <label className="text-xs font-bold block">De</label>
                <Input type="date" value={adminStart} onChange={e => setAdminStart(e.target.value)} data-testid="admin-start" />
              </div>
              <div>
                <label className="text-xs font-bold block">Até</label>
                <Input type="date" value={adminEnd} onChange={e => setAdminEnd(e.target.value)} data-testid="admin-end" />
              </div>
              <Button size="sm" variant="outline" onClick={() => { setAdminStart(""); setAdminEnd(""); }}>Limpar</Button>
              <Button size="sm" variant="outline" onClick={() => {
                const now = new Date();
                setAdminStart(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
                setAdminEnd(now.toISOString().slice(0, 10));
              }}>Mês atual</Button>
              <Button size="sm" variant="outline" onClick={() => {
                const now = new Date();
                setAdminStart(new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10));
                setAdminEnd(now.toISOString().slice(0, 10));
              }}>Ano atual</Button>
              <div className="ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openFiscalDlg}
                  data-testid="admin-fiscal-config-btn"
                  className="mr-2"
                >
                  <FileDown className="h-4 w-4 mr-1" />
                  Config Fiscal (NFC-e)
                </Button>
                <Button
                  size="sm"
                  onClick={exportAdminPdf}
                  disabled={!adminReport || exportingPdf}
                  data-testid="admin-export-pdf-btn"
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                >
                  <FileDown className="h-4 w-4 mr-1" />
                  {exportingPdf ? "Gerando PDF..." : "Exportar PDF"}
                </Button>
              </div>
            </div>
            {adminReport ? (
              <div ref={adminPanelRef} data-testid="admin-panel-content" className="space-y-3 bg-white p-2">
                {/* FINANCEIRO */}
                <h3 className="font-bold text-sm uppercase mt-2">💰 Financeiro</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  <Card><CardContent className="p-3"><p className="text-xs">Vendas</p><p className="text-xl font-bold text-blue-700">{adminReport.financial.total_sales_count}</p></CardContent></Card>
                  <Card className="bg-green-50"><CardContent className="p-3"><p className="text-xs">Receita</p><p className="text-xl font-bold text-green-700">{fmt(adminReport.financial.total_revenue)}</p></CardContent></Card>
                  <Card className="bg-red-50"><CardContent className="p-3"><p className="text-xs">Custo</p><p className="text-xl font-bold text-red-700">{fmt(adminReport.financial.total_cost)}</p></CardContent></Card>
                  <Card className="bg-purple-50"><CardContent className="p-3"><p className="text-xs">Lucro Bruto · Margem</p><p className="text-xl font-bold text-purple-700">{fmt(adminReport.financial.total_profit)}</p><p className="text-xs">{adminReport.financial.margin_percent}%</p></CardContent></Card>
                  <Card className="bg-emerald-50"><CardContent className="p-3"><p className="text-xs">Lucro Líquido (após comissão)</p><p className="text-xl font-bold text-emerald-700">{fmt(adminReport.financial.net_profit_after_commission)}</p><p className="text-xs">Comissão: {fmt(adminReport.financial.total_commission)}</p></CardContent></Card>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Card className="bg-amber-50"><CardContent className="p-3"><p className="text-xs">A Receber (A Prazo)</p><p className="text-lg font-bold text-amber-700">{fmt(adminReport.financial.receivable)}</p></CardContent></Card>
                  <Card><CardContent className="p-3"><p className="text-xs">Vendas À Prazo (Total)</p><p className="text-lg font-bold">{fmt(adminReport.financial.aprazo_total)}</p></CardContent></Card>
                  <Card><CardContent className="p-3"><p className="text-xs">Já Recebido (A Prazo)</p><p className="text-lg font-bold text-green-700">{fmt(adminReport.financial.aprazo_paid)}</p></CardContent></Card>
                  <Card><CardContent className="p-3"><p className="text-xs">Desconto Concedido</p><p className="text-lg font-bold text-red-600">{fmt(adminReport.financial.total_discount)}</p></CardContent></Card>
                </div>

                {/* MINI-GRÁFICOS DE EVOLUÇÃO MENSAL */}
                <h3 className="font-bold text-sm uppercase mt-3">📈 Evolução Mensal</h3>
                {monthlyChartData.length === 0 ? (
                  <p className="text-center text-sm py-4 border rounded bg-gray-50">Sem dados no período para gerar gráficos.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-charts">
                    <Card>
                      <CardHeader className="pb-1"><CardTitle className="text-sm font-bold">Receita Mensal</CardTitle></CardHeader>
                      <CardContent className="p-2" style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monthlyChartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => fmt(v)} />
                            <Bar dataKey="revenue" fill="#16a34a" name="Receita" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-1"><CardTitle className="text-sm font-bold">Lucro Mensal</CardTitle></CardHeader>
                      <CardContent className="p-2" style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyChartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => fmt(v)} />
                            <Line type="monotone" dataKey="profit" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} name="Lucro" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-1"><CardTitle className="text-sm font-bold">Produção Mensal</CardTitle></CardHeader>
                      <CardContent className="p-2" style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monthlyChartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="production" fill="#d97706" name="Unidades" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-1"><CardTitle className="text-sm font-bold">Comissões Mensais</CardTitle></CardHeader>
                      <CardContent className="p-2" style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyChartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => fmt(v)} />
                            <Line type="monotone" dataKey="commission" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Comissões" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* MÉTODOS DE PAGAMENTO */}
                <h3 className="font-bold text-sm uppercase mt-3">💳 Por Forma de Pagamento</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(adminReport.by_payment_method || {}).map(([k, v]) => (
                    <Card key={k}><CardContent className="p-3">
                      <p className="text-xs uppercase">{PAYMENT_LABEL[k] || k}</p>
                      <p className="font-bold">{fmt(v.revenue)}</p>
                      <p className="text-xs">{v.count} venda(s)</p>
                    </CardContent></Card>
                  ))}
                </div>

                {/* LUCRATIVIDADE POR PRODUTO */}
                <h3 className="font-bold text-sm uppercase mt-3">📦 Lucratividade por Produto</h3>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-100"><tr>
                      <th className="text-left p-2">Produto</th>
                      <th className="text-right p-2">Qtd</th>
                      <th className="text-right p-2">Receita</th>
                      <th className="text-right p-2">Custo</th>
                      <th className="text-right p-2">Lucro</th>
                      <th className="text-right p-2">Margem</th>
                    </tr></thead>
                    <tbody>
                      {(adminReport.products_profitability || []).map(p => (
                        <tr key={p.product_id} className="border-t">
                          <td className="p-2">{p.product_name}</td>
                          <td className="p-2 text-right">{p.qty} {p.unit}</td>
                          <td className="p-2 text-right">{fmt(p.revenue)}</td>
                          <td className="p-2 text-right text-red-700">{fmt(p.cost)}</td>
                          <td className="p-2 text-right font-bold text-green-700">{fmt(p.profit)}</td>
                          <td className="p-2 text-right">{p.margin}%</td>
                        </tr>
                      ))}
                      {(adminReport.products_profitability || []).length === 0 && <tr><td colSpan={6} className="p-4 text-center">Sem vendas</td></tr>}
                    </tbody>
                  </table>
                </div>

                {/* VENDEDORES */}
                <h3 className="font-bold text-sm uppercase mt-3">👤 Vendedores</h3>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-100"><tr>
                      <th className="text-left p-2">Vendedor</th>
                      <th className="text-right p-2">Vendas</th>
                      <th className="text-right p-2">Receita</th>
                      <th className="text-right p-2">Lucro</th>
                      <th className="text-right p-2">Comissão (4%)</th>
                    </tr></thead>
                    <tbody>
                      {(adminReport.sellers || []).map(s => (
                        <tr key={s.seller_id || s.seller_name} className="border-t">
                          <td className="p-2">{s.seller_name}{s.seller_role ? <span className="text-xs text-gray-500"> · {s.seller_role}</span> : null}</td>
                          <td className="p-2 text-right">{s.sales_count}</td>
                          <td className="p-2 text-right">{fmt(s.revenue)}</td>
                          <td className="p-2 text-right text-green-700">{fmt(s.profit)}</td>
                          <td className="p-2 text-right font-bold text-emerald-700">{fmt(s.commission)}</td>
                        </tr>
                      ))}
                      {(adminReport.sellers || []).length === 0 && <tr><td colSpan={5} className="p-4 text-center">Sem vendedores</td></tr>}
                    </tbody>
                  </table>
                </div>

                {/* RANKING DE OPERADORES PDV */}
                <h3 className="font-bold text-sm uppercase mt-3 flex items-center gap-2">
                  🏆 Ranking de Operadores (PDV)
                  <Select value={operatorPeriod} onValueChange={setOperatorPeriod}>
                    <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Hoje</SelectItem>
                      <SelectItem value="month">Este mês</SelectItem>
                    </SelectContent>
                  </Select>
                </h3>
                {operatorRanking && (
                  <div className="border rounded-lg overflow-x-auto" data-testid="operator-ranking">
                    <table className="w-full text-sm min-w-[700px]">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-2 w-10">#</th>
                          <th className="text-left p-2">Operador</th>
                          <th className="text-right p-2">Vendas</th>
                          <th className="text-right p-2">Receita</th>
                          <th className="text-right p-2">Dinheiro</th>
                          <th className="text-right p-2">PIX</th>
                          <th className="text-right p-2">Débito</th>
                          <th className="text-right p-2">Crédito</th>
                          <th className="text-right p-2">A Prazo</th>
                          <th className="text-right p-2">Lucro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(operatorRanking.ranking || []).map((o, i) => (
                          <tr key={o.user_id || i} className="border-t">
                            <td className="p-2 font-bold">
                              {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                            </td>
                            <td className="p-2 font-bold">{o.user_name}</td>
                            <td className="p-2 text-right">{o.sales_count}</td>
                            <td className="p-2 text-right font-bold text-emerald-700">{fmt(o.revenue)}</td>
                            <td className="p-2 text-right text-xs">{fmt(o.by_method.dinheiro)}</td>
                            <td className="p-2 text-right text-xs">{fmt(o.by_method.pix)}</td>
                            <td className="p-2 text-right text-xs">{fmt(o.by_method.cartao_debito)}</td>
                            <td className="p-2 text-right text-xs">{fmt(o.by_method.cartao_credito)}</td>
                            <td className="p-2 text-right text-xs">{fmt(o.by_method.aprazo)}</td>
                            <td className="p-2 text-right font-bold text-indigo-700">{fmt(o.profit)}</td>
                          </tr>
                        ))}
                        {(operatorRanking.ranking || []).length === 0 && (
                          <tr><td colSpan={10} className="p-4 text-center text-gray-500">Nenhuma venda no PDV neste período</td></tr>
                        )}
                      </tbody>
                      {(operatorRanking.ranking || []).length > 0 && (
                        <tfoot className="bg-gray-50 border-t-2">
                          <tr>
                            <td colSpan={2} className="p-2 font-bold text-right">TOTAL</td>
                            <td className="p-2 text-right font-bold">{operatorRanking.totals?.sales_count}</td>
                            <td className="p-2 text-right font-bold text-emerald-700">{fmt(operatorRanking.totals?.revenue)}</td>
                            <td colSpan={5}></td>
                            <td className="p-2 text-right font-bold text-indigo-700">{fmt(operatorRanking.totals?.profit)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}

                {/* COMPRAS / FORNECEDORES */}
                <h3 className="font-bold text-sm uppercase mt-3">🧾 Compras & Fornecedores</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Card><CardContent className="p-3"><p className="text-xs">Compras Totais</p><p className="text-lg font-bold">{fmt(adminReport.purchases.total)}</p><p className="text-xs">{adminReport.purchases.count} entrada(s)</p></CardContent></Card>
                  <Card className="bg-green-50"><CardContent className="p-3"><p className="text-xs">À Vista</p><p className="text-lg font-bold text-green-700">{fmt(adminReport.purchases.total_avista)}</p></CardContent></Card>
                  <Card className="bg-amber-50"><CardContent className="p-3"><p className="text-xs">A Prazo</p><p className="text-lg font-bold text-amber-700">{fmt(adminReport.purchases.total_aprazo)}</p></CardContent></Card>
                </div>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead className="bg-gray-100"><tr>
                      <th className="text-left p-2">Fornecedor</th>
                      <th className="text-right p-2">Entradas</th>
                      <th className="text-right p-2">Total</th>
                    </tr></thead>
                    <tbody>
                      {(adminReport.purchases.by_supplier || []).map((s, i) => (
                        <tr key={s.supplier_id || s.supplier_name || `sup-${i}`} className="border-t">
                          <td className="p-2">{s.supplier_name}</td>
                          <td className="p-2 text-right">{s.count}</td>
                          <td className="p-2 text-right font-bold">{fmt(s.total)}</td>
                        </tr>
                      ))}
                      {(adminReport.purchases.by_supplier || []).length === 0 && <tr><td colSpan={3} className="p-4 text-center">Sem compras</td></tr>}
                    </tbody>
                  </table>
                </div>

                {/* ESTOQUE */}
                <h3 className="font-bold text-sm uppercase mt-3">📊 Estoque Atual</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Card><CardContent className="p-3"><p className="text-xs">Produtos</p><p className="text-lg font-bold">{adminReport.stock.products_count} itens</p><p className="text-xs">{adminReport.stock.products_qty_total} unidades totais</p></CardContent></Card>
                  <Card className="bg-blue-50"><CardContent className="p-3"><p className="text-xs">Valor em Produtos</p><p className="text-lg font-bold text-blue-700">{fmt(adminReport.stock.products_value)}</p></CardContent></Card>
                  <Card><CardContent className="p-3"><p className="text-xs">Matérias-Primas</p><p className="text-lg font-bold">{adminReport.stock.raw_materials_count} itens</p></CardContent></Card>
                  <Card className="bg-indigo-50"><CardContent className="p-3"><p className="text-xs">Valor Total Estoque</p><p className="text-lg font-bold text-indigo-700">{fmt(adminReport.stock.total_value)}</p></CardContent></Card>
                </div>

                {/* PRODUÇÃO */}
                <h3 className="font-bold text-sm uppercase mt-3">🏭 Produção</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Card className="bg-amber-50"><CardContent className="p-3"><p className="text-xs">Unidades Produzidas</p><p className="text-xl font-bold text-amber-700">{adminReport.production.produced_total}</p><p className="text-xs">{adminReport.production.count} lote(s)</p></CardContent></Card>
                  <Card><CardContent className="p-3"><p className="text-xs">Ajustes / Migração</p><p className="text-xl font-bold">{adminReport.production.adjusted_total}</p></CardContent></Card>
                </div>
              </div>
            ) : <p className="text-center py-6">Carregando painel administrativo...</p>}
          </TabsContent>
        )}
      </Tabs>

      {/* ----- DIALOG MATERIA-PRIMA ----- */}
      <Dialog open={matDlg.open} onOpenChange={(o) => !o && setMatDlg({ open: false, data: null, id: null })}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{matDlg.id ? "Editar" : "Nova"} Matéria-Prima</DialogTitle></DialogHeader>
          {matDlg.data && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold">Nome *</label>
                <Input value={matDlg.data.name} onChange={e => setMatDlg({ ...matDlg, data: { ...matDlg.data, name: e.target.value } })} data-testid="mat-name" />
              </div>
              <div>
                <label className="text-xs font-bold">Descrição</label>
                <Input value={matDlg.data.description || ""} onChange={e => setMatDlg({ ...matDlg, data: { ...matDlg.data, description: e.target.value } })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-bold">Unidade</label>
                  <Select value={matDlg.data.unit || "kg"} onValueChange={v => setMatDlg({ ...matDlg, data: { ...matDlg.data, unit: v } })}>
                    <SelectTrigger data-testid="mat-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="g">Grama (g)</SelectItem>
                      <SelectItem value="kg">Quilograma (kg)</SelectItem>
                      <SelectItem value="ton">Tonelada (ton)</SelectItem>
                      <SelectItem value="ml">Mililitro (ml)</SelectItem>
                      <SelectItem value="L">Litro (L)</SelectItem>
                      <SelectItem value="m">Metro (m)</SelectItem>
                      <SelectItem value="m2">Metro² (m²)</SelectItem>
                      <SelectItem value="m3">Metro³ (m³)</SelectItem>
                      <SelectItem value="un">Unidade (un)</SelectItem>
                      <SelectItem value="saco">Saco</SelectItem>
                      <SelectItem value="cx">Caixa (cx)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">Custo por un.</label>
                  <Input type="number" step="0.01" value={matDlg.data.unit_cost} onChange={e => setMatDlg({ ...matDlg, data: { ...matDlg.data, unit_cost: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Estoque inicial</label>
                  <Input type="number" step="0.01" value={matDlg.data.quantity} onChange={e => setMatDlg({ ...matDlg, data: { ...matDlg.data, quantity: e.target.value } })} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold">Fornecedor</label>
                <Select value={matDlg.data.supplier_id || "none"} onValueChange={v => setMatDlg({ ...matDlg, data: { ...matDlg.data, supplier_id: v === "none" ? "" : v } })}>
                  <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sem fornecedor —</SelectItem>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatDlg({ open: false, data: null, id: null })}>Cancelar</Button>
            <Button className="bg-blue-500 hover:bg-blue-600" onClick={saveMat} data-testid="mat-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- DIALOG PRODUTO ----- */}
      <Dialog open={prodDlg.open} onOpenChange={(o) => !o && setProdDlg({ open: false, data: null, id: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{prodDlg.id ? "Editar" : "Novo"} Produto Industrializado</DialogTitle></DialogHeader>
          {prodDlg.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2">
                  <label className="text-xs font-bold">Nome *</label>
                  <Input value={prodDlg.data.name} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, name: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Unidade</label>
                  <Select value={prodDlg.data.unit || "saco"} onValueChange={v => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, unit: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="g">Grama (g)</SelectItem>
                      <SelectItem value="kg">Quilograma (kg)</SelectItem>
                      <SelectItem value="ton">Tonelada (ton)</SelectItem>
                      <SelectItem value="ml">Mililitro (ml)</SelectItem>
                      <SelectItem value="L">Litro (L)</SelectItem>
                      <SelectItem value="m3">Metro³ (m³)</SelectItem>
                      <SelectItem value="saco">Saco</SelectItem>
                      <SelectItem value="un">Unidade (un)</SelectItem>
                      <SelectItem value="cx">Caixa (cx)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold">Descrição</label>
                <Input value={prodDlg.data.description || ""} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, description: e.target.value } })} />
              </div>
              <div>
                <label className="text-xs font-bold">Código de Barras / SKU (opcional, usado no PDV)</label>
                <Input value={prodDlg.data.barcode || ""} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, barcode: e.target.value } })} placeholder="7891234567890" data-testid="prod-barcode" />
              </div>
              <div className="border rounded p-2" data-testid="prod-photo-section">
                <label className="text-xs font-bold block mb-1">Foto do produto (aparece no Catálogo)</label>
                {prodDlg.id
                  ? <ProductPhotoUpload companyId={companyId} product={{ id: prodDlg.id, name: prodDlg.data.name, catalog_photo_id: prodDlg.data.catalog_photo_id }} onChanged={fetchAll} />
                  : <p className="text-xs text-gray-600">Salve o produto primeiro; depois clique em Editar para enviar a foto.</p>}
              </div>
              <div>
                <label className="text-xs font-bold">Modo de Custo</label>
                <Select value={prodDlg.data.cost_mode} onValueChange={v => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, cost_mode: v } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recipe">Receita (soma matérias-primas)</SelectItem>
                    <SelectItem value="manual">Custo digitado manualmente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {prodDlg.data.cost_mode === "manual" ? (
                <div>
                  <label className="text-xs font-bold">Custo Manual por unidade</label>
                  <Input type="number" step="0.01" value={prodDlg.data.manual_cost} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, manual_cost: e.target.value } })} />
                </div>
              ) : (
                <div className="border rounded p-2">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-bold">Receita (matérias-primas por unidade do produto)</label>
                    <Button size="sm" variant="outline" onClick={addRecipeItem}><Plus className="h-3 w-3 mr-1" />Linha</Button>
                  </div>
                  <div className="space-y-2">
                    {(prodDlg.data.recipe || []).map((r, idx) => (
                      <div key={`${r.raw_material_id || 'empty'}-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-7">
                          <Select value={r.raw_material_id || "none"} onValueChange={v => updateRecipeItem(idx, "raw_material_id", v === "none" ? "" : v)}>
                            <SelectTrigger><SelectValue placeholder="Matéria-prima" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Selecione —</SelectItem>
                              {materials.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit} · {fmt(m.unit_cost)})</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4">
                          <Input type="number" step="0.001" placeholder="Qtd" value={r.quantity} onChange={e => updateRecipeItem(idx, "quantity", e.target.value)} />
                        </div>
                        <div className="col-span-1">
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeRecipeItem(idx)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-bold">Despesas (R$ / un.)</label>
                  <Input type="number" step="0.01" value={prodDlg.data.overhead_cost} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, overhead_cost: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Imposto (%)</label>
                  <Input type="number" step="0.01" value={prodDlg.data.tax_percent} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, tax_percent: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Margem de Lucro (%)</label>
                  <Input type="number" step="0.01" value={prodDlg.data.profit_margin_percent} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, profit_margin_percent: e.target.value } })} />
                </div>
              </div>
              <Card className="bg-blue-50">
                <CardContent className="p-3 grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-[11px]">Custo base</p><p className="font-bold">{fmt(previewCost.base)}</p></div>
                  <div><p className="text-[11px]">Custo c/ imposto</p><p className="font-bold">{fmt(previewCost.unit_cost)}</p></div>
                  <div><p className="text-[11px]">Preço sugerido</p><p className="font-bold text-blue-700">{fmt(previewCost.suggested)}</p></div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-bold">Preço Atacado</label>
                  <Input type="number" step="0.01" value={prodDlg.data.price_wholesale} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, price_wholesale: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Preço Varejo</label>
                  <Input type="number" step="0.01" value={prodDlg.data.price_retail} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, price_retail: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Preço Especial</label>
                  <Input type="number" step="0.01" value={prodDlg.data.price_special} onChange={e => setProdDlg({ ...prodDlg, data: { ...prodDlg.data, price_special: e.target.value } })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProdDlg({ open: false, data: null, id: null })}>Cancelar</Button>
            <Button className="bg-indigo-500 hover:bg-indigo-600" onClick={saveProd} data-testid="prod-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- DIALOG PRODUCAO ----- */}
      <Dialog open={productionDlg.open} onOpenChange={(o) => !o && setProductionDlg({ open: false, product_id: "", quantity: "", is_adjustment: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar Produção</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold">Produto</label>
              <Select value={productionDlg.product_id || "none"} onValueChange={v => setProductionDlg({ ...productionDlg, product_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Selecione —</SelectItem>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold">
                {productionDlg.is_adjustment ? "Quantidade do ajuste (use valor negativo para dar baixa)" : "Quantidade a produzir"}
              </label>
              <Input type="number" step="0.01" value={productionDlg.quantity} onChange={e => setProductionDlg({ ...productionDlg, quantity: e.target.value })} data-testid="production-quantity" />
              {productionDlg.is_adjustment && parseFloat(productionDlg.quantity) < 0 && (
                <div className="text-xs text-red-600 mt-1">Ajuste negativo: {Math.abs(parseFloat(productionDlg.quantity))} un serão retiradas do estoque</div>
              )}
            </div>
            <label className="flex items-start gap-2 cursor-pointer bg-amber-50 border border-amber-200 rounded p-2">
              <input type="checkbox" checked={!!productionDlg.is_adjustment}
                onChange={e => setProductionDlg({ ...productionDlg, is_adjustment: e.target.checked })}
                data-testid="production-is-adjustment" className="mt-1" />
              <span className="text-xs">
                <b>Estoque Inicial / Ajuste (migração)</b><br />
                Marque para somar <b>OU subtrair</b> do estoque <b>SEM</b> consumir matéria-prima. Use valor negativo para baixar (ex: -5 remove 5 un).
              </span>
            </label>
            <div>
              <label className="text-xs font-bold">
                Observações
                {productionDlg.is_adjustment && parseFloat(productionDlg.quantity) < 0 && (
                  <span className="text-red-600 ml-1">* (motivo da baixa — obrigatório)</span>
                )}
              </label>
              <Input
                value={productionDlg.notes || ""}
                onChange={e => setProductionDlg({ ...productionDlg, notes: e.target.value })}
                placeholder={productionDlg.is_adjustment && parseFloat(productionDlg.quantity) < 0 ? "Ex: quebra, perda, erro de contagem…" : ""}
                data-testid="production-notes"
                className={productionDlg.is_adjustment && parseFloat(productionDlg.quantity) < 0 && !(productionDlg.notes || "").trim() ? "border-red-400" : ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductionDlg({ open: false, product_id: "", quantity: "", is_adjustment: false })}>Cancelar</Button>
            <Button className={productionDlg.is_adjustment ? "bg-amber-500 hover:bg-amber-600" : "bg-amber-500 hover:bg-amber-600"} onClick={confirmProduction} data-testid="production-save">
              {productionDlg.is_adjustment
                ? (parseFloat(productionDlg.quantity) < 0 ? "Dar Baixa no Estoque" : "Lançar Estoque / Ajuste +")
                : "Produzir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- DIALOG VENDA ----- */}
      <Dialog open={saleDlg.open} onOpenChange={(o) => !o && setSaleDlg({ open: false, data: null })}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{saleDlg.editing_id ? "Editar Venda (Admin)" : "Nova Venda"}</DialogTitle></DialogHeader>
          {saleDlg.editing_id && saleDlg.last_edit && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900" data-testid="sale-last-edit-info">
              Última edição por <b>{saleDlg.last_edit.by}</b> em {saleDlg.last_edit.at}
            </div>
          )}
          {saleDlg.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold">Cliente cadastrado</label>
                  <Select value={saleDlg.data.client_id || "none"} onValueChange={v => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, client_id: v === "none" ? "" : v } })}>
                    <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Consumidor —</SelectItem>
                      {(saleDlg.data.payment_method === "aprazo"
                        ? clientsCredit.filter(c => c.credit_status === "approved")
                        : clients).map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                          {saleDlg.data.payment_method === "aprazo" && c.credit_limit ? ` · Lim: ${fmt(c.available_limit || 0)} · ${c.max_days}d · ${c.max_installments}x` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {saleDlg.data.payment_method === "aprazo" && clientsCredit.filter(c => c.credit_status === "approved").length === 0 && (
                    <p className="text-xs text-red-700 mt-1">Nenhum cliente com crédito aprovado. Vá à aba Clientes/Crédito.</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-bold">Ou nome manual</label>
                  <Input value={saleDlg.data.client_name_manual} onChange={e => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, client_name_manual: e.target.value } })}
                    disabled={saleDlg.data.payment_method === "aprazo"}
                    placeholder={saleDlg.data.payment_method === "aprazo" ? "A prazo exige cliente cadastrado" : ""} />
                </div>
              </div>
              <div className="border rounded p-2">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-bold">Itens</label>
                  <Button size="sm" variant="outline" onClick={addSaleItem}><Plus className="h-3 w-3 mr-1" />Item</Button>
                </div>
                <div className="space-y-2">
                  {saleDlg.data.items.map((it, idx) => (
                    <div key={`${it.product_id || 'empty'}-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5">
                        <Select value={it.product_id || "none"} onValueChange={v => updateSaleItem(idx, "product_id", v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="Produto" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Selecione —</SelectItem>
                            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} (estoque: {p.quantity})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" step="0.01" placeholder="Qtd" value={it.quantity} onChange={e => updateSaleItem(idx, "quantity", e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <Select value={it.price_type} onValueChange={v => updateSaleItem(idx, "price_type", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="atacado">Atacado</SelectItem>
                            <SelectItem value="varejo">Varejo</SelectItem>
                            <SelectItem value="especial">Especial</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" step="0.01" placeholder="Preço un." value={it.unit_price} onChange={e => updateSaleItem(idx, "unit_price", e.target.value)} />
                      </div>
                      <div className="col-span-1">
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeSaleItem(idx)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-bold">Desconto</label>
                  <Input type="number" step="0.01" value={saleDlg.data.discount} onChange={e => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, discount: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Frete (R$)</label>
                  <Input type="number" step="0.01" value={saleDlg.data.freight || ""} onChange={e => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, freight: e.target.value } })} data-testid="sale-freight" />
                </div>
                <div className="flex items-end justify-end">
                  <Badge className="text-base bg-green-600">Total: {fmt(saleTotal)}</Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 border-t pt-3">
                <div>
                  <label className="text-xs font-bold">Tipo de Pagamento *</label>
                  <Select value={saleDlg.data.payment_method} onValueChange={v => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, payment_method: v } })}>
                    <SelectTrigger data-testid="sale-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="cartao">Cartão</SelectItem>
                      <SelectItem value="aprazo">A Prazo</SelectItem>
                    </SelectContent>
                  </Select>
                  {saleDlg.editing_id && <div className="text-xs text-amber-700 mt-1">Admin: trocar o tipo recria a transação (reverte banco antigo, credita o novo ou gera parcelas).</div>}
                </div>
                {["pix", "cartao"].includes(saleDlg.data.payment_method) && (
                  <div>
                    <label className="text-xs font-bold">Conta bancária que recebe *</label>
                    <Select value={saleDlg.data.bank_account_id || "none"} onValueChange={v => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, bank_account_id: v === "none" ? "" : v } })}>
                      <SelectTrigger data-testid="sale-bank-account"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Selecione —</SelectItem>
                        {(bankAccounts || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {saleDlg.data.payment_method === "aprazo" && (
                  <>
                    <div>
                      <label className="text-xs font-bold">Nº de Parcelas *</label>
                      <Input type="number" min="1" max="36" value={saleDlg.data.installments_count}
                        onChange={e => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, installments_count: e.target.value } })}
                        data-testid="sale-installments" />
                    </div>
                    <div>
                      <label className="text-xs font-bold">1º Vencimento *</label>
                      <Input type="date" value={saleDlg.data.first_due_date}
                        onChange={e => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, first_due_date: e.target.value } })}
                        data-testid="sale-first-due" />
                    </div>
                  </>
                )}
              </div>
              {saleDlg.data.payment_method === "aprazo" && parseInt(saleDlg.data.installments_count) > 0 && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                  {parseInt(saleDlg.data.installments_count)}x de {fmt(saleTotal / parseInt(saleDlg.data.installments_count))}
                  {" · "}1ª parcela em {saleDlg.data.first_due_date ? new Date(saleDlg.data.first_due_date + "T00:00:00").toLocaleDateString("pt-BR") : "—"}
                  {(() => {
                    const c = clientsCredit.find(x => x.id === saleDlg.data.client_id);
                    if (!c || c.credit_status !== "approved") return null;
                    return (
                      <div className="mt-1 text-xs">
                        <b>Crédito do cliente:</b> Limite {fmt(c.credit_limit)} · Disponível {fmt(c.available_limit)} · Máx {c.max_days}d · Máx {c.max_installments}x
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t pt-3">
                <div>
                  <label className="text-xs font-bold">Vendedor (Colaborador)</label>
                  <Select value={saleDlg.data.seller_id || "none"} onValueChange={v => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, seller_id: v === "none" ? "" : v, seller_name_manual: v === "none" ? saleDlg.data.seller_name_manual : "" } })}>
                    <SelectTrigger data-testid="sale-seller"><SelectValue placeholder="Opcional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nenhum / usar nome manual —</SelectItem>
                      {employees.filter(e => e.status !== "inativo").map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">Ou Vendedor Externo</label>
                  <Input value={saleDlg.data.seller_name_manual || ""}
                    onChange={e => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, seller_name_manual: e.target.value, seller_id: "" } })}
                    placeholder="Nome do vendedor não cadastrado"
                    disabled={!!saleDlg.data.seller_id} />
                </div>
              </div>
              {(saleDlg.data.seller_id || saleDlg.data.seller_name_manual) && saleTotal > 0 && (
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
                  Comissão (4%): <b>{fmt(saleTotal * 0.04)}</b>
                </div>
              )}

              <div>
                <label className="text-xs font-bold">Observações</label>
                <Input value={saleDlg.data.notes || ""} onChange={e => setSaleDlg({ ...saleDlg, data: { ...saleDlg.data, notes: e.target.value } })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaleDlg({ open: false, data: null })}>Cancelar</Button>
            <Button className="bg-green-500 hover:bg-green-600" onClick={submitSale} data-testid="sale-save">{saleDlg.editing_id ? "Salvar alterações" : "Registrar Venda"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ----- DIALOG PARCELAS ----- */}
      <Dialog open={instDlg.open} onOpenChange={(o) => !o && setInstDlg({ open: false, sale: null, items: [] })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Parcelas · Venda #{instDlg.sale ? String(instDlg.sale.sale_number).padStart(4, "0") : ""}
              {instDlg.sale ? ` · ${instDlg.sale.client_name || "Consumidor"}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Nº</th>
                  <th className="text-left p-2">Vencimento</th>
                  <th className="text-right p-2">Valor</th>
                  <th className="text-center p-2">Status</th>
                  <th className="text-left p-2">Pago em</th>
                  <th className="text-center p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {instDlg.items.map(i => {
                  const overdue = i.status === "pendente" && new Date(i.due_date) < new Date(new Date().toDateString());
                  return (
                    <tr key={i.id} className="border-t">
                      <td className="p-2 font-mono">{i.installment_number}/{i.total_installments}</td>
                      <td className="p-2">{new Date(i.due_date + "T00:00:00").toLocaleDateString("pt-BR")}</td>
                      <td className="p-2 text-right font-bold">{fmt(i.amount)}</td>
                      <td className="p-2 text-center">
                        {i.status === "pago"
                          ? <Badge className="bg-green-600">Pago</Badge>
                          : overdue ? <Badge className="bg-red-600">Vencida</Badge> : <Badge className="bg-amber-500">Pendente</Badge>}
                      </td>
                      <td className="p-2 text-xs">{(i.paid_at || i.paid_date) ? new Date(i.paid_at || i.paid_date).toLocaleDateString("pt-BR") : "—"}</td>
                      <td className="p-2 text-center">
                        {i.status === "pendente"
                          ? <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => askPayInstallment(i)} data-testid={`pay-inst-${i.id}`}>Dar baixa</Button>
                          : (
                            <div className="flex gap-1 justify-center items-center">
                              <Badge className="bg-green-700">Recebida</Badge>
                              {isAdmin && (
                                <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => revertInstallmentPayment(i)} data-testid={`revert-inst-${i.id}`} title="Estornar pagamento (admin)">
                                  Estornar
                                </Button>
                              )}
                            </div>
                          )}
                      </td>
                    </tr>
                  );
                })}
                {instDlg.items.length === 0 && <tr><td colSpan={6} className="p-6 text-center">Sem parcelas</td></tr>}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstDlg({ open: false, sale: null, items: [] })}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ----- DIALOG CONFIRMAR BAIXA DE PARCELA ----- */}
      <Dialog open={payDlg.open} onOpenChange={(o) => !o && setPayDlg({ open: false, inst: null, bank_account_id: "" })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Dar baixa na parcela</DialogTitle></DialogHeader>
          {payDlg.inst && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm">
                Parcela <b>{payDlg.inst.installment_number}/{payDlg.inst.total_installments || "?"}</b> · Valor <b>{fmt(payDlg.inst.amount)}</b>
                <br />Venc.: {new Date(payDlg.inst.due_date + "T00:00:00").toLocaleDateString("pt-BR")}
              </div>
              <div>
                <label className="text-xs font-bold">Conta bancária que receberá o valor *</label>
                <Select value={payDlg.bank_account_id || "none"} onValueChange={v => setPayDlg({ ...payDlg, bank_account_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="pay-bank-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Selecione —</SelectItem>
                    {bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.is_default ? " (padrão)" : ""} · {fmt(b.current_balance || 0)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg({ open: false, inst: null, bank_account_id: "" })}>Cancelar</Button>
            <Button className="bg-green-500 hover:bg-green-600" onClick={confirmPayInstallment} data-testid="pay-confirm">Confirmar Baixa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ----- DIALOG APROVAR CRÉDITO ----- */}
      <Dialog open={creditDlg.open} onOpenChange={(o) => !o && setCreditDlg({ open: false, client: null, credit_limit: "", max_days: 20, max_installments: 1, notes: "" })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aprovar Crédito · {creditDlg.client?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold">Limite de Crédito (R$) *</label>
              <Input type="number" step="0.01" value={creditDlg.credit_limit}
                onChange={e => setCreditDlg({ ...creditDlg, credit_limit: e.target.value })}
                data-testid="credit-limit-input" />
              <p className="text-xs text-gray-500 mt-1">Deixe 0 para permitir qualquer valor dentro do prazo</p>
            </div>
            <div>
              <label className="text-xs font-bold">Prazo Máximo (dias) *</label>
              <Select value={String(creditDlg.max_days)} onValueChange={v => setCreditDlg({ ...creditDlg, max_days: parseInt(v) })}>
                <SelectTrigger data-testid="credit-max-days"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20 dias</SelectItem>
                  <SelectItem value="45">45 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold">Máximo de Parcelas *</label>
              <Select value={String(creditDlg.max_installments)} onValueChange={v => setCreditDlg({ ...creditDlg, max_installments: parseInt(v) })}>
                <SelectTrigger data-testid="credit-max-installments"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1x</SelectItem>
                  <SelectItem value="2">2x</SelectItem>
                  <SelectItem value="3">3x</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold">Observações</label>
              <Input value={creditDlg.notes} onChange={e => setCreditDlg({ ...creditDlg, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDlg({ open: false, client: null, credit_limit: "", max_days: 20, max_installments: 1, notes: "" })}>Cancelar</Button>
            <Button className="bg-green-500 hover:bg-green-600" onClick={saveCredit} data-testid="credit-save">Aprovar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ----- DIALOG ENTRADA / COMPRA DE MP ----- */}
      <Dialog open={entryDlg.open} onOpenChange={(o) => !o && setEntryDlg({ open: false, data: null })}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nova Entrada / Compra de Matéria-Prima</DialogTitle></DialogHeader>
          {entryDlg.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2">
                  <label className="text-xs font-bold">Fornecedor *</label>
                  <Select value={entryDlg.data.supplier_id || "none"} onValueChange={v => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, supplier_id: v === "none" ? "" : v } })}>
                    <SelectTrigger data-testid="entry-supplier"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Selecione —</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">Nº Nota Fiscal</label>
                  <Input value={entryDlg.data.invoice_number} onChange={e => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, invoice_number: e.target.value } })} />
                </div>
              </div>

              <div className="border rounded p-2">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-bold">Itens Comprados</label>
                  <Button size="sm" variant="outline" onClick={entryAddItem}><Plus className="h-3 w-3 mr-1" />Item</Button>
                </div>
                <div className="space-y-2">
                  {entryDlg.data.items.map((it, idx) => (
                    <div key={`${it.raw_material_id || 'empty'}-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5">
                        <Select value={it.raw_material_id || "none"} onValueChange={v => entryUpdateItem(idx, "raw_material_id", v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="Matéria-prima" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Selecione —</SelectItem>
                            {materials.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" step="0.01" placeholder="Qtd" value={it.quantity} onChange={e => entryUpdateItem(idx, "quantity", e.target.value)} />
                      </div>
                      <div className="col-span-3">
                        <Input type="number" step="0.0001" placeholder="Custo un." value={it.unit_cost} onChange={e => entryUpdateItem(idx, "unit_cost", e.target.value)} />
                      </div>
                      <div className="col-span-1 text-right text-xs font-bold">
                        {fmt((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0))}
                      </div>
                      <div className="col-span-1">
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => entryRemoveItem(idx)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-2 font-bold">Total: {fmt(entryTotal)}</div>
              </div>

              <div>
                <label className="text-xs font-bold">Forma de Pagamento *</label>
                <Select value={entryDlg.data.payment_method} onValueChange={v => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, payment_method: v } })}>
                  <SelectTrigger data-testid="entry-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avista">À Vista</SelectItem>
                    <SelectItem value="aprazo">A Prazo (com boletos)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {entryDlg.data.payment_method === "avista" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-bold">Conta Bancária (débito) *</label>
                    <Select value={entryDlg.data.bank_account_id || "none"} onValueChange={v => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, bank_account_id: v === "none" ? "" : v } })}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Selecione —</SelectItem>
                        {bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.is_default ? " (padrão)" : ""} · {fmt(b.current_balance || 0)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-bold">Data do Pagamento</label>
                    <Input type="date" value={entryDlg.data.first_due_date} onChange={e => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, first_due_date: e.target.value } })} />
                  </div>
                </div>
              ) : (
                <div className="border rounded p-2 bg-amber-50">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-xs font-bold">Nº de Parcelas *</label>
                    <Input type="number" min="1" max="24" value={entryDlg.data.installments_count}
                      className="w-24" onChange={e => entrySetInstallmentsCount(e.target.value)} />
                  </div>
                  <table className="w-full text-sm">
                    <thead><tr>
                      <th className="text-center p-1">Nº</th>
                      <th className="text-left p-1">Vencimento</th>
                      <th className="text-right p-1">Valor</th>
                      <th className="text-center p-1">Boleto (PDF/Imagem)</th>
                    </tr></thead>
                    <tbody>
                      {(entryDlg.data.installments || []).map((i, idx) => (
                        <tr key={`parc-${i.installment_number || idx}`} className="border-t">
                          <td className="p-1 text-center font-bold">{i.installment_number}</td>
                          <td className="p-1">
                            <Input type="date" value={i.due_date} onChange={e => entryUpdateInstallment(idx, "due_date", e.target.value)} />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="0.01" className="text-right" value={i.amount} onChange={e => entryUpdateInstallment(idx, "amount", e.target.value)} />
                          </td>
                          <td className="p-1 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <input type="file" accept="application/pdf,image/*" style={{ display: "none" }}
                                id={`boleto-upload-${idx}`}
                                onChange={e => entryUploadBoleto(idx, e.target.files?.[0])} />
                              <Button size="sm" variant="outline" type="button"
                                onClick={() => document.getElementById(`boleto-upload-${idx}`).click()}
                                data-testid={`entry-upload-boleto-${idx}`}>
                                {i.boleto_url ? "Trocar" : "Anexar"}
                              </Button>
                              {i.boleto_filename && <span className="text-xs text-green-700">✓ {i.boleto_filename.slice(0, 20)}</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="text-right mt-2 text-xs">
                    Soma parcelas: <b>{fmt((entryDlg.data.installments || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0))}</b> · Total: <b>{fmt(entryTotal)}</b>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-bold">Observações</label>
                <Input value={entryDlg.data.notes || ""} onChange={e => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, notes: e.target.value } })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryDlg({ open: false, data: null })}>Cancelar</Button>
            <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={submitEntry} data-testid="entry-save">Registrar Entrada</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ----- DIALOG 2ª VIA ----- */}
      <Dialog open={!!dupReceipt} onOpenChange={(o) => !o && setDupReceipt(null)}>
        <DialogContent className="max-w-md" data-testid="duplicate-receipt-dialog">
          <div ref={dupReceiptRef}>
            <SaleReceiptContent receipt={dupReceipt} duplicate />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupReceipt(null)}>Fechar</Button>
            <Button
              onClick={() => { if (!printReceiptNode(dupReceiptRef.current, `2a via #${dupReceipt?.sale_number || ""}`)) toast.error("Habilite popups para imprimir"); }}
              data-testid="duplicate-receipt-print"
            >
              <Printer className="h-4 w-4 mr-1" />Imprimir 2ª Via
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- DIALOG META ----- */}
      <Dialog open={goalDlg.open} onOpenChange={(o) => !o && setGoalDlg({ open: false, data: null, id: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{goalDlg.id ? "Editar" : "Nova"} Meta</DialogTitle></DialogHeader>
          {goalDlg.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold">Ano *</label>
                  <Input type="number" value={goalDlg.data.year} onChange={e => setGoalDlg({ ...goalDlg, data: { ...goalDlg.data, year: e.target.value } })} data-testid="goal-year" />
                </div>
                <div>
                  <label className="text-xs font-bold">Mês (vazio = ano todo)</label>
                  <Select value={goalDlg.data.month ? String(goalDlg.data.month) : "none"} onValueChange={v => setGoalDlg({ ...goalDlg, data: { ...goalDlg.data, month: v === "none" ? "" : parseInt(v) } })}>
                    <SelectTrigger data-testid="goal-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Anual —</SelectItem>
                      {["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"].map((m, i) => (
                        <SelectItem key={m} value={String(i+1)}>{String(i+1).padStart(2,"0")} - {m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold">Meta de Receita (R$) *</label>
                <Input type="number" step="0.01" value={goalDlg.data.target_revenue} onChange={e => setGoalDlg({ ...goalDlg, data: { ...goalDlg.data, target_revenue: e.target.value } })} data-testid="goal-target" />
              </div>
              <div>
                <label className="text-xs font-bold">Meta de Nº de Vendas (opcional)</label>
                <Input type="number" value={goalDlg.data.target_sales_count} onChange={e => setGoalDlg({ ...goalDlg, data: { ...goalDlg.data, target_sales_count: e.target.value } })} />
              </div>
              <div>
                <label className="text-xs font-bold">Vendedor (opcional — meta por vendedor)</label>
                <Select value={goalDlg.data.seller_id || "none"} onValueChange={v => setGoalDlg({ ...goalDlg, data: { ...goalDlg.data, seller_id: v === "none" ? "" : v, product_id: v === "none" ? goalDlg.data.product_id : "" } })}>
                  <SelectTrigger><SelectValue placeholder="Geral" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Geral —</SelectItem>
                    {employees.filter(e => e.status !== "inativo").map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold">Produto (opcional — meta por produto)</label>
                <Select value={goalDlg.data.product_id || "none"} onValueChange={v => setGoalDlg({ ...goalDlg, data: { ...goalDlg.data, product_id: v === "none" ? "" : v, seller_id: v === "none" ? goalDlg.data.seller_id : "" } })}>
                  <SelectTrigger><SelectValue placeholder="Qualquer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Qualquer —</SelectItem>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold">Observações</label>
                <Input value={goalDlg.data.notes} onChange={e => setGoalDlg({ ...goalDlg, data: { ...goalDlg.data, notes: e.target.value } })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalDlg({ open: false, data: null, id: null })}>Cancelar</Button>
            <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={saveGoal} data-testid="goal-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ----- DIALOG META DE PRODUÇÃO ----- */}
      <Dialog open={prodGoalDlg.open} onOpenChange={(o) => !o && setProdGoalDlg({ open: false, data: null, id: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{prodGoalDlg.id ? "Editar" : "Nova"} Meta de Produção</DialogTitle></DialogHeader>
          {prodGoalDlg.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold">Ano *</label>
                  <Input type="number" value={prodGoalDlg.data.year} onChange={e => setProdGoalDlg({ ...prodGoalDlg, data: { ...prodGoalDlg.data, year: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-bold">Mês (vazio = anual)</label>
                  <Select value={prodGoalDlg.data.month ? String(prodGoalDlg.data.month) : "none"} onValueChange={v => setProdGoalDlg({ ...prodGoalDlg, data: { ...prodGoalDlg.data, month: v === "none" ? "" : parseInt(v) } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Anual —</SelectItem>
                      {["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"].map((m, i) => (
                        <SelectItem key={m} value={String(i+1)}>{String(i+1).padStart(2,"0")} - {m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold">Produto *</label>
                <Select value={prodGoalDlg.data.product_id || "none"} onValueChange={v => setProdGoalDlg({ ...prodGoalDlg, data: { ...prodGoalDlg.data, product_id: v === "none" ? "" : v } })}>
                  <SelectTrigger data-testid="prod-goal-product"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Selecione —</SelectItem>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold">Meta de Unidades a Produzir *</label>
                <Input type="number" step="0.01" value={prodGoalDlg.data.target_quantity} onChange={e => setProdGoalDlg({ ...prodGoalDlg, data: { ...prodGoalDlg.data, target_quantity: e.target.value } })} data-testid="prod-goal-qty" />
              </div>
              <div>
                <label className="text-xs font-bold">Observações</label>
                <Input value={prodGoalDlg.data.notes} onChange={e => setProdGoalDlg({ ...prodGoalDlg, data: { ...prodGoalDlg.data, notes: e.target.value } })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProdGoalDlg({ open: false, data: null, id: null })}>Cancelar</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={saveProdGoal} data-testid="prod-goal-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Sangria / Suprimento ===== */}
      <Dialog open={movDlg.open} onOpenChange={(v) => { if (!v) setMovDlg({ open: false, type: "sangria", amount: "", reason: "", bank_account_id: "" }); }}>
        <DialogContent className="max-w-md" data-testid="cash-movement-dialog">
          <DialogHeader><DialogTitle>{movDlg.type === "sangria" ? "Sangria (retirada do caixa)" : "Suprimento (reforço do caixa)"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-xs bg-gray-50 border rounded p-2">
              {movDlg.type === "sangria"
                ? "Dinheiro saindo do caixa físico. Se informar uma conta bancária, lança um depósito como Entrada no sistema."
                : "Dinheiro entrando no caixa físico. Se informar uma conta bancária, lança um saque como Saída no sistema."}
            </div>
            <div>
              <label className="text-xs font-bold block">Valor (R$) *</label>
              <Input type="number" step="0.01" value={movDlg.amount} onChange={e => setMovDlg({ ...movDlg, amount: e.target.value })} autoFocus data-testid="mov-amount" />
            </div>
            <div>
              <label className="text-xs font-bold block">Motivo</label>
              <Input value={movDlg.reason} onChange={e => setMovDlg({ ...movDlg, reason: e.target.value })} placeholder={movDlg.type === "sangria" ? "Ex: depósito no banco" : "Ex: troco inicial adicional"} />
            </div>
            <div>
              <label className="text-xs font-bold block">Conta Bancária (opcional)</label>
              <Select value={movDlg.bank_account_id || "__none__"} onValueChange={v => setMovDlg({ ...movDlg, bank_account_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="mov-bank"><SelectValue placeholder="Nenhuma (não lançar no banco)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhuma (só caixa físico) —</SelectItem>
                  {(bankAccounts || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovDlg({ open: false, type: "sangria", amount: "", reason: "", bank_account_id: "" })}>Cancelar</Button>
            <Button className={movDlg.type === "sangria" ? "bg-amber-500 hover:bg-amber-600" : "bg-sky-500 hover:bg-sky-600"} onClick={saveCashMovement} data-testid="mov-save">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Fechar Caixa ===== */}
      <Dialog open={closeSessionDlg.open} onOpenChange={(v) => { if (!v) setCloseSessionDlg({ open: false, session: null, counted: "", notes: "" }); }}>
        <DialogContent className="max-w-md" data-testid="close-session-dialog">
          <DialogHeader><DialogTitle>Fechar Caixa</DialogTitle></DialogHeader>
          <p className="text-sm">Conte o dinheiro físico presente no caixa e informe abaixo. O sistema compara com o esperado.</p>
          <div>
            <label className="text-xs font-bold block">Dinheiro contado (R$)</label>
            <Input type="number" step="0.01" value={closeSessionDlg.counted} onChange={e => setCloseSessionDlg({ ...closeSessionDlg, counted: e.target.value })} data-testid="close-counted" />
          </div>
          <div>
            <label className="text-xs font-bold block">Observações</label>
            <Input value={closeSessionDlg.notes} onChange={e => setCloseSessionDlg({ ...closeSessionDlg, notes: e.target.value })} placeholder="Ex: sangria realizada..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseSessionDlg({ open: false, session: null, counted: "", notes: "" })}>Cancelar</Button>
            <Button className="bg-red-500 hover:bg-red-600" onClick={closeCashSession} data-testid="confirm-close-cash">Fechar e Conferir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Configuração Fiscal (NFC-e) ===== */}
      <Dialog open={fiscalDlg.open} onOpenChange={(v) => { if (!v) setFiscalDlg({ open: false, data: null }); }}>
        <DialogContent className="max-w-2xl" data-testid="fiscal-config-dialog">
          <DialogHeader><DialogTitle>Configuração Fiscal — NFC-e (Focus NFe)</DialogTitle></DialogHeader>
          {fiscalDlg.data && (
            <div className="space-y-2">
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                Preencha com os dados fiscais da empresa emitente. Esses dados são usados por todas as emissões de NFC-e via Focus NFe. Confirme com seu contador.
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-xs font-bold block">CNPJ Emitente *</label>
                  <Input value={fiscalDlg.data.cnpj_emitente || ""} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, cnpj_emitente: e.target.value.replace(/\D/g, "") }})} placeholder="Somente números" data-testid="fiscal-cnpj" />
                </div>
                <div>
                  <label className="text-xs font-bold block">UF</label>
                  <Input value={fiscalDlg.data.uf || ""} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, uf: e.target.value.toUpperCase() }})} maxLength={2} />
                </div>
                <div>
                  <label className="text-xs font-bold block">Código Município (IBGE 7 díg)</label>
                  <Input value={fiscalDlg.data.municipio_codigo || ""} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, municipio_codigo: e.target.value }})} />
                </div>
                <div>
                  <label className="text-xs font-bold block">Regime Tributário</label>
                  <Select value={String(fiscalDlg.data.regime_tributario || 1)} onValueChange={v => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, regime_tributario: Number(v) }})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Simples Nacional</SelectItem>
                      <SelectItem value="2">Simples - Excesso</SelectItem>
                      <SelectItem value="3">Regime Normal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold block">NCM Padrão</label>
                  <Input value={fiscalDlg.data.ncm_default || ""} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, ncm_default: e.target.value }})} placeholder="38245000" />
                </div>
                <div>
                  <label className="text-xs font-bold block">CFOP Padrão</label>
                  <Input value={fiscalDlg.data.cfop_default || ""} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, cfop_default: e.target.value }})} placeholder="5102" />
                </div>
                <div>
                  <label className="text-xs font-bold block">CSOSN/CST Padrão</label>
                  <Input value={fiscalDlg.data.csosn_default || ""} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, csosn_default: e.target.value }})} placeholder="102" />
                </div>
                <div>
                  <label className="text-xs font-bold block">Origem (0-8)</label>
                  <Input type="number" min={0} max={8} value={fiscalDlg.data.origem_default ?? 0} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, origem_default: Number(e.target.value) }})} />
                </div>
                <div>
                  <label className="text-xs font-bold block">Unidade Padrão</label>
                  <Input value={fiscalDlg.data.unidade_default || "SC"} onChange={e => setFiscalDlg({ ...fiscalDlg, data: { ...fiscalDlg.data, unidade_default: e.target.value.toUpperCase() }})} />
                </div>
              </div>
              <div className="text-xs text-gray-600">
                <b>Antes de emitir:</b> cadastre no painel Focus NFe a empresa emitente (CNPJ, IE, endereço, certificado A1 e CSC homologação/produção) e defina a variável <code>FOCUS_NFE_TOKEN</code> no backend.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFiscalDlg({ open: false, data: null })}>Cancelar</Button>
            <Button className="bg-sky-500 hover:bg-sky-600" onClick={saveFiscalConfig} data-testid="fiscal-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Frente de Caixa (PDV) ===== */}
      <FactoryPOS
        open={posOpen}
        onClose={() => setPosOpen(false)}
        companyId={companyId}
        companyName={companyName}
        products={products}
        employees={employees}
        clients={clients}
        clientsCredit={clientsCredit}
        bankAccounts={bankAccounts}
        onCreated={fetchAll}
      />
    </div>
  );
}
